// src/pages/publicRCData/rcCalculations.js
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../utils/firebase";

/**
 * Central calculations file for Recovery Capital data analysis
 * This file handles all calculations and data processing to ensure consistent numbers
 * across different components of the application.
 * ALL VALUES ARE CALCULATED DYNAMICALLY FROM FIREBASE DATA - NO HARDCODED VALUES
 */

// Helper functions
const hasActiveCravings = (v) => {
  const n = Number(v);
  return n >= 3; // This means: if n is greater than OR EQUAL TO 3
};

const checkResponse = (v) => {
  const normalizedValue = String(v || "")
    .toLowerCase()
    .trim();
  if (normalizedValue.startsWith("y")) return "yes";
  if (
    normalizedValue.startsWith("n") &&
    !normalizedValue.includes("n/a") &&
    !normalizedValue.includes("n/")
  )
    return "no";
  return "na"; // For N/A, undefined, or other values
};

const hasGuiltShame = (v) => {
  const n = Number(v);
  return n === 3 || n === 4;
};

const isInTherapy = (v) => {
  const n = Number(v);
  return n === 3 || n === 4;
};

const hasStrongKidsRelation = (v) => {
  const n = Number(v);
  return n === 3 || n === 4;
};

const isRelapse = (s) =>
  ["not sober"].includes(
    String(s || "")
      .toLowerCase()
      .trim()
  );

const byUID = (d) => d.residentId;

// Calculate statistical correlation between two arrays
const calculateCorrelation = (x, y) => {
  if (!x.length || x.length !== y.length) return null;
  const n = x.length;
  const mx = x.reduce((s, v) => s + v, 0) / n;
  const my = y.reduce((s, v) => s + v, 0) / n;
  let num = 0,
    dx = 0,
    dy = 0;

  for (let i = 0; i < n; i++) {
    const a = x[i] - mx;
    const b = y[i] - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }

  return dx && dy ? num / Math.sqrt(dx * dy) : null;
};

// Calculate effect size (Cohen's d) between two groups
const calculateEffectSize = (group1, group2) => {
  if (group1.length === 0 || group2.length === 0) return 0;

  const mean1 = group1.reduce((sum, val) => sum + val, 0) / group1.length;
  const mean2 = group2.reduce((sum, val) => sum + val, 0) / group2.length;

  const variance1 =
    group1.reduce((sum, val) => sum + Math.pow(val - mean1, 2), 0) /
    group1.length;
  const variance2 =
    group2.reduce((sum, val) => sum + Math.pow(val - mean2, 2), 0) /
    group2.length;

  const pooledSD = Math.sqrt((variance1 + variance2) / 2);

  return pooledSD > 0 ? (mean1 - mean2) / pooledSD : 0;
};

// Calculate percentile from array of values
const calculatePercentile = (values, percentile) => {
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, index)];
};

/**
 * FIXED: Core metric calculation function with safety checks
 */
const buildMetric = (exp, ref, expRC, refRC) => {
  // ✅ SAFETY CHECK: Ensure inputs exist
  if (
    !exp ||
    !ref ||
    typeof exp.c === "undefined" ||
    typeof ref.c === "undefined"
  ) {
    console.warn("buildMetric called with undefined buckets:", { exp, ref });

    // Calculate rates here so they're available
    const actualExpRate = exp && exp.c ? exp.r / exp.c : 0;
    const actualRefRate = ref && ref.c ? ref.r / ref.c : 0;

    return {
      or: 1.0,
      rateExp: Math.round(actualExpRate * 100),
      rateRef: Math.round(actualRefRate * 100),
      ciLo: 0.1,
      ciHi: 10,
      strength: "low",
      smallSample: true,
      sampleSizes: { with: exp?.c || 0, without: ref?.c || 0 },
    };
  }

  // Calculate actual rates (move this up from line 361)
  const actualExpRate = exp.c ? exp.r / exp.c : 0;
  const actualRefRate = ref.c ? ref.r / ref.c : 0;

  // Check if we have zero cells
  const hasZeros =
    exp.r === 0 || exp.c - exp.r === 0 || ref.r === 0 || ref.c - ref.r === 0;

  // Check if samples are small
  const smallSample = exp.c < 10 || ref.c < 10;

  // Only apply continuity correction if we have zeros AND small samples
  const needsCorrection = hasZeros && smallSample;

  if (!exp.c && !ref.c) return null;

  let or, ciLo, ciHi, strength;

  if (needsCorrection) {
    // Apply continuity correction only when needed
    const a = exp.r + 0.5;
    const b = exp.c - exp.r + 0.5;
    const c = ref.r + 0.5;
    const d = ref.c - ref.r + 0.5;

    const expRateForOR = a / (a + b);
    const refRateForOR = c / (c + d);

    or =
      expRateForOR / (1 - expRateForOR) / (refRateForOR / (1 - refRateForOR));

    if (!isFinite(or) || isNaN(or)) {
      or = 100;
    }

    const se = Math.sqrt(1 / a + 1 / b + 1 / c + 1 / d);
    ciLo = Math.exp(Math.log(or) - 1.96 * se);
    ciHi = Math.exp(Math.log(or) + 1.96 * se);

    strength = "low"; // Always low confidence for corrected small samples
  } else if (hasZeros && !smallSample) {
    // Large sample with zeros - handle specially
    if (exp.r === 0 && ref.r > 0) {
      // Highly protective factor - calculate upper bound
      const upperBound = 3 / exp.c; // Rule of 3
      or = upperBound / actualRefRate;
      ciLo = 0.01; // Very small lower bound
      ciHi = or * 3; // Conservative upper bound
      strength = "moderate";
    } else if (ref.r === 0 && exp.r > 0) {
      // Highly risky factor
      const upperBound = 3 / ref.c;
      or = actualExpRate / upperBound;
      ciLo = or / 3;
      ciHi = 100;
      strength = "moderate";
    } else {
      // Both groups have zeros - insufficient data
      or = 1;
      ciLo = 0.1;
      ciHi = 10;
      strength = "low";
    }
  } else {
    // Standard calculation - no zeros, sufficient sample
    if (
      actualExpRate === 0 ||
      actualRefRate === 0 ||
      actualExpRate === 1 ||
      actualRefRate === 1
    ) {
      // Edge case: rates are exactly 0 or 1
      or =
        actualExpRate === actualRefRate
          ? 1
          : actualExpRate > actualRefRate
          ? 10
          : 0.1;
      ciLo = or / 5;
      ciHi = or * 5;
      strength = "low";
    } else {
      // Normal calculation with raw data
      or =
        actualExpRate /
        (1 - actualExpRate) /
        (actualRefRate / (1 - actualRefRate));

      // Calculate standard error using raw counts
      const se = Math.sqrt(
        1 / exp.r + 1 / (exp.c - exp.r) + 1 / ref.r + 1 / (ref.c - ref.r)
      );
      ciLo = Math.exp(Math.log(or) - 1.96 * se);
      ciHi = Math.exp(Math.log(or) + 1.96 * se);

      // Assess strength based on confidence interval and sample size
      if (smallSample) {
        strength = "low";
      } else if (ciLo > 1 && ciHi < 5) {
        strength = "high";
      } else if (ciLo > 1 || ciHi < 1) {
        strength = "moderate";
      } else {
        strength = "low";
      }
    }
  }

  // ADD: Calculate statistical significance
  const calculateStatisticalSignificance = (a, b, c, d) => {
    const n = a + b + c + d;
    if (n < 5)
      return {
        chi2: 0,
        pValue: "> 0.05",
        significant: false,
        note: "Sample too small",
      };

    const expected_a = ((a + b) * (a + c)) / n;
    const expected_b = ((a + b) * (b + d)) / n;
    const expected_c = ((a + c) * (c + d)) / n;
    const expected_d = ((b + d) * (c + d)) / n;

    // Check if expected frequencies are adequate for chi-square test
    if (expected_a < 5 || expected_b < 5 || expected_c < 5 || expected_d < 5) {
      return {
        chi2: 0,
        pValue: "> 0.05",
        significant: false,
        note: "Expected frequencies too low",
      };
    }

    const chi2 =
      Math.pow(a - expected_a, 2) / expected_a +
      Math.pow(b - expected_b, 2) / expected_b +
      Math.pow(c - expected_c, 2) / expected_c +
      Math.pow(d - expected_d, 2) / expected_d;

    // Critical value for p < 0.05 with 1 df is 3.84
    const significant = chi2 > 3.84;
    const pValue = chi2 > 6.63 ? "< 0.01" : chi2 > 3.84 ? "< 0.05" : "> 0.05";

    return {
      chi2: +chi2.toFixed(3),
      pValue,
      significant,
      note: significant
        ? "Statistically significant"
        : "Not statistically significant",
    };
  };

  // Calculate RC difference if available
  let rcDiff = undefined;
  if (expRC && refRC && expRC.c > 0 && refRC.c > 0) {
    const rcExp = expRC.rc / expRC.c;
    const rcRef = refRC.rc / refRC.c;
    rcDiff = rcExp - rcRef;
  }

  // Calculate statistical significance using 2x2 contingency table
  const statistics = calculateStatisticalSignificance(
    Math.round(exp.r),
    Math.round(exp.c - exp.r),
    Math.round(ref.r),
    Math.round(ref.c - ref.r)
  );

  return {
    or,
    rateExp: Math.round(actualExpRate * 100), // Always show actual rates
    rateRef: Math.round(actualRefRate * 100), // Always show actual rates
    ciLo,
    ciHi,
    strength,
    rcDiff,
    smallSample,
    hasZeros,
    correctionApplied: needsCorrection, // Flag for transparency
    sampleSizes: {
      with: Math.round(exp.c),
      without: Math.round(ref.c),
    },
    // ADD: Statistical significance results
    statistics,
    confidenceInterval: `${ciLo.toFixed(2)} - ${ciHi.toFixed(2)}`,
    statisticallySignificant: statistics.significant,
  };
};

/**
 * Helper function to normalize metrics consistently for display
 */
const normalizeMetric = (metric, shouldBeProtective) => {
  if (!metric) return null;

  const isCurrentlyProtective = metric.or < 1;

  if (isCurrentlyProtective === shouldBeProtective) {
    return metric;
  }

  return {
    ...metric,
    or: 1 / metric.or,
    rateExp: metric.rateRef,
    rateRef: metric.rateExp,
    rcDiff: metric.rcDiff * -1,
    strength: metric.strength,
    smallSample: metric.smallSample,
    sampleSizes: {
      with: metric.sampleSizes?.without,
      without: metric.sampleSizes?.with,
    },
  };
};

/**
 * DYNAMIC FACTOR COMBINATIONS CALCULATION
 * Calculates all possible combinations of risk factors based on actual data
 */
const calculateFactorCombinations = (rawData, alumniMap, weightSum) => {
  const factors = {
    trauma: {
      field: "childhoodTrauma",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    cod: {
      field: "coOccurringDiagnosis",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    prison: {
      field: "overOneYearInPrison",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    homeless: {
      field: "homelessOneYear",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    strongKidsRelation: {
      field: "strongRelationKids",
      check: (val, row) => {
        return checkResponse(row.kids) === "yes" && Number(val) >= 3; // Good relationship
      },
      isValid: (val, row) => {
        const kidsResponse = checkResponse(row.kids);
        return kidsResponse === "yes" || kidsResponse === "no";
      },
    },
    purpose: {
      field: ["lifeHasPurpose", "greaterPurpose"],
      check: (row) => {
        const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
          .map((f) => +row[f])
          .filter((v) => !Number.isNaN(v));
        return (
          purposeItems.length &&
          purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3
        );
      },
      // Purpose doesn't need validation since it uses numeric scales
    },
    twoParentHome: {
      field: "twoParentsHome",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    guiltShame: {
      field: "guiltOrShame",
      check: (val) => hasGuiltShame(val),
      // Guilt doesn't need validation since it uses numeric scales
    },
    therapy: {
      field: "therapy",
      check: (val) => isInTherapy(val),
      // Therapy doesn't need validation since it uses numeric scales
    },
  };

  // Calculate all two-factor combinations
  const combinations = {};
  const factorNames = Object.keys(factors);

  for (let i = 0; i < factorNames.length; i++) {
    for (let j = i + 1; j < factorNames.length; j++) {
      const factorA = factorNames[i];
      const factorB = factorNames[j];
      const combKey = `${factorA}${
        factorB.charAt(0).toUpperCase() + factorB.slice(1)
      }`;

      const buckets = {
        bothFactors: { c: 0, r: 0, rc: 0 },
        factorAOnly: { c: 0, r: 0, rc: 0 },
        factorBOnly: { c: 0, r: 0, rc: 0 },
        neitherFactor: { c: 0, r: 0, rc: 0 },
      };

      rawData.forEach((row) => {
        const uid = byUID(row);
        const w = 1 / weightSum[uid];
        const alumni = alumniMap.get(uid) || {};
        const relapsed = isRelapse(alumni.sobrietyStatus);
        const rcTotal = +row.recoveryCapitalTotal || 0;

        // Validate responses for both factors before processing
        let factorAValid = true;
        let factorBValid = true;

        // Check Factor A validity
        if (factors[factorA].isValid) {
          if (Array.isArray(factors[factorA].field)) {
            factorAValid = true; // Purpose doesn't need validation
          } else {
            factorAValid = factors[factorA].isValid(
              row[factors[factorA].field],
              row
            );
          }
        }

        // Check Factor B validity
        if (factors[factorB].isValid) {
          if (Array.isArray(factors[factorB].field)) {
            factorBValid = true; // Purpose doesn't need validation
          } else {
            factorBValid = factors[factorB].isValid(
              row[factors[factorB].field],
              row
            );
          }
        }

        // Skip this row if either factor has unknown response
        if (!factorAValid || !factorBValid) {
          return;
        }

        // Check if resident has each factor
        let hasFactorA, hasFactorB;

        if (Array.isArray(factors[factorA].field)) {
          hasFactorA = factors[factorA].check(row);
        } else {
          hasFactorA = factors[factorA].check(row[factors[factorA].field], row);
        }

        if (Array.isArray(factors[factorB].field)) {
          hasFactorB = factors[factorB].check(row);
        } else {
          hasFactorB = factors[factorB].check(row[factors[factorB].field], row);
        }

        // Categorize into buckets
        let bucket;
        if (hasFactorA && hasFactorB) bucket = buckets.bothFactors;
        else if (hasFactorA && !hasFactorB) bucket = buckets.factorAOnly;
        else if (!hasFactorA && hasFactorB) bucket = buckets.factorBOnly;
        else bucket = buckets.neitherFactor;

        bucket.c += w;
        bucket.rc += w * rcTotal;
        if (relapsed) bucket.r += w;
      });

      // Calculate metrics for this combination
      const calculateBucketMetrics = (bucket) => ({
        rcDiff:
          bucket.c > 0
            ? bucket.rc / bucket.c -
              buckets.neitherFactor.rc / Math.max(1, buckets.neitherFactor.c)
            : 0,
        relapse: bucket.c > 0 ? Math.round((bucket.r / bucket.c) * 100) : 0,
        count: Math.round(bucket.c),
      });

      // Only include combinations that have meaningful data
      if (
        buckets.bothFactors.c > 0 ||
        buckets.factorAOnly.c > 0 ||
        buckets.factorBOnly.c > 0
      ) {
        combinations[combKey] = {
          factorA: factorA.charAt(0).toUpperCase() + factorA.slice(1),
          factorB: factorB.charAt(0).toUpperCase() + factorB.slice(1),
          bothFactors: calculateBucketMetrics(buckets.bothFactors),
          factorAOnly: calculateBucketMetrics(buckets.factorAOnly),
          factorBOnly: calculateBucketMetrics(buckets.factorBOnly),
          neitherFactor: calculateBucketMetrics(buckets.neitherFactor),
        };
      }
    }
  }

  return combinations;
};

/**
 * Resident-weighted domain-impact calculator
 * Each resident appears exactly once (rowsForStats is already averaged-per-resident)
 */
const createDomainImpacts = (rowsForStats) => {
  const domains = ["spiritual", "social", "individual", "physical", "general"];

  const factors = {
    trauma: {
      name: "Trauma Impact",
      icon: "AlertTriangle",
      color: "bg-red-500",
      field: "childhoodTrauma",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    guilt: {
      name: "Has Guilt/Shame",
      icon: "Heart",
      color: "bg-purple-500",
      field: "guiltOrShame",
      check: (val) => hasGuiltShame(val),
      // No isValid needed - uses numeric scale
    },
    purpose: {
      name: "Purpose & Meaning",
      icon: "Award",
      color: "bg-blue-500",
      check: (row) => {
        const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
          .map((f) => +row[f])
          .filter((v) => !Number.isNaN(v));
        return (
          purposeItems.length &&
          purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3
        );
      },
      // No isValid needed - uses numeric scale
    },
    twoParentHome: {
      name: "Two-Parent Home",
      icon: "Home",
      color: "bg-emerald-500",
      field: "twoParentsHome",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    cod: {
      name: "Co-occurring Diagnosis",
      icon: "Brain",
      color: "bg-red-500",
      field: "coOccurringDiagnosis",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    prison: {
      name: "Prison Experience",
      icon: "Shield",
      color: "bg-amber-500",
      field: "overOneYearInPrison",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    homeless: {
      name: "Homelessness",
      icon: "Home",
      color: "bg-orange-500",
      field: "homelessOneYear",
      check: (val) => checkResponse(val) === "yes",
      isValid: (val) => {
        const response = checkResponse(val);
        return response === "yes" || response === "no";
      },
    },
    therapy: {
      name: "In Therapy",
      icon: "Users",
      color: "bg-green-500",
      field: "therapy",
      check: (val) => isInTherapy(val),
      // No isValid needed - uses numeric scale
    },
    strongKidsRelation: {
      name: "Strong Kids Relationship",
      icon: "Heart",
      color: "bg-pink-500",
      field: "strongRelationKids",
      check: (val, row) => {
        return checkResponse(row.kids) === "yes" && hasStrongKidsRelation(val);
      },
      isValid: (val, row) => {
        const kidsResponse = checkResponse(row.kids);
        return kidsResponse === "yes" || kidsResponse === "no";
      },
    },
  };

  const domainImpacts = {};

  Object.entries(factors).forEach(([factorKey, factor]) => {
    const domainEffects = [];

    domains.forEach((domain) => {
      const withFactor = [];
      const withoutFactor = [];

      rowsForStats.forEach((row) => {
        const domainScore = +row[`${domain}CapitalScore`] || 0;
        if (domainScore === 0) return;

        // VALIDATE RESPONSE FIRST (if factor needs validation)
        if (factor.isValid && factor.field) {
          if (!factor.isValid(row[factor.field], row)) {
            return; // Skip this row - unknown response
          }
        }

        const hasFactor = factor.field
          ? factor.check(row[factor.field], row)
          : factor.check(row);

        (hasFactor ? withFactor : withoutFactor).push(domainScore);
      });

      const withMean =
        withFactor.length > 0
          ? withFactor.reduce((sum, v) => sum + v, 0) / withFactor.length
          : 0;
      const withoutMean =
        withoutFactor.length > 0
          ? withoutFactor.reduce((sum, v) => sum + v, 0) / withoutFactor.length
          : 0;

      domainEffects.push({
        domain,
        impact: +(withMean - withoutMean).toFixed(1),
        withCount: withFactor.length,
        withoutCount: withoutFactor.length,
        effectSize: calculateEffectSize(withFactor, withoutFactor),
      });
    });

    domainImpacts[factorKey] = {
      ...factor,
      domains: domainEffects,
    };
  });

  return domainImpacts;
};

/**
 * DYNAMIC BENCHMARKING - Calculate percentiles from your own data
 */
const calculateBenchmarkComparisons = (rowsForStats) => {
  const rcScores = rowsForStats.map((r) => +r.recoveryCapitalTotal || 0);
  const yourAverage =
    rcScores.reduce((sum, score) => sum + score, 0) /
    Math.max(1, rcScores.length);

  // Calculate dynamic benchmarks from your data
  const benchmarks = {
    averageRC: +yourAverage.toFixed(1),
    lowPerformingRC: calculatePercentile(rcScores, 25),
    averagePerformingRC: calculatePercentile(rcScores, 50),
    highPerformingRC: calculatePercentile(rcScores, 75),
    topTierRC: calculatePercentile(rcScores, 90),
    domainAverages: {
      spiritual: calculatePercentile(
        rowsForStats
          .map((r) => +r.spiritualCapitalScore || 0)
          .filter((s) => s > 0),
        50
      ),
      social: calculatePercentile(
        rowsForStats
          .map((r) => +r.socialCapitalScore || 0)
          .filter((s) => s > 0),
        50
      ),
      individual: calculatePercentile(
        rowsForStats
          .map((r) => +r.individualCapitalScore || 0)
          .filter((s) => s > 0),
        50
      ),
      physical: calculatePercentile(
        rowsForStats
          .map((r) => +r.physicalCapitalScore || 0)
          .filter((s) => s > 0),
        50
      ),
      general: calculatePercentile(
        rowsForStats
          .map((r) => +r.generalCapitalScore || 0)
          .filter((s) => s > 0),
        50
      ),
    },
  };

  const percentileRanking = {
    p25: calculatePercentile(rcScores, 25),
    p50: calculatePercentile(rcScores, 50),
    p75: calculatePercentile(rcScores, 75),
    p90: calculatePercentile(rcScores, 90),
  };

  return {
    yourPerformance: {
      averageRC: +yourAverage.toFixed(1),
      percentileRanking,
    },
    internalComparison: {
      ranking:
        yourAverage >= benchmarks.topTierRC
          ? "Top Tier"
          : yourAverage >= benchmarks.highPerformingRC
          ? "Above Average"
          : yourAverage >= benchmarks.averagePerformingRC
          ? "Average"
          : "Below Average",
    },
    benchmarks,
  };
};

/**
 * DYNAMIC RISK STRATIFICATION - Calculate weights from odds ratios
 */
const calculateRiskStratificationScientific = (
  rowsForStats,
  alumniMap,
  riskMetrics
) => {
  // 1️⃣ Calculate empirically-derived weights through cross-validation
  const calculateEmpiricalWeights = (residents, outcomes) => {
    const weights = {};

    // For each factor, calculate its actual predictive power
    Object.entries(riskMetrics).forEach(([factor, metric]) => {
      if (!metric || metric.or <= 1) return;

      // Use standardized log-odds ratio as weight
      const logOR = Math.log(metric.or);

      // Apply confidence adjustment - reduce weight for uncertain estimates
      const confidenceAdjustment =
        metric.strength === "high"
          ? 1.0
          : metric.strength === "moderate"
          ? 0.7
          : 0.4;

      // Sample size adjustment - reduce weight for small samples
      const totalSample =
        (metric.sampleSizes?.with || 0) + (metric.sampleSizes?.without || 0);
      const sampleAdjustment = Math.min(1.0, totalSample / 30); // Full weight at n=30+

      weights[factor] = logOR * confidenceAdjustment * sampleAdjustment;
    });

    return weights;
  };

  const empiricalWeights = calculateEmpiricalWeights(rowsForStats, alumniMap);

  // 2️⃣ Calculate validated risk scores
  const riskScores = rowsForStats.map((row) => {
    const residentName =
      row.residentName ||
      row.fullName ||
      `${row.firstName} ${row.lastName}`.trim();
    const alumni = alumniMap.get(residentName) || {};
    const relapsed = isRelapse(alumni.sobrietyStatus);

    let riskScore = 0;
    const riskFactors = [];
    const factorContributions = {}; // Track individual contributions

    // Apply empirically-weighted risk factors
    Object.entries(empiricalWeights).forEach(([factor, weight]) => {
      let hasFactor = false;
      let factorName = "";
      let hasValidResponse = true; // NEW

      switch (factor) {
        case "trauma":
          const traumaResponse = checkResponse(row.childhoodTrauma);
          if (traumaResponse !== "yes" && traumaResponse !== "no") {
            hasValidResponse = false;
          } else {
            hasFactor = traumaResponse === "yes";
            factorName = "Childhood Trauma";
          }
          break;
        case "cod":
          const codResponse = checkResponse(row.coOccurringDiagnosis);
          if (codResponse !== "yes" && codResponse !== "no") {
            hasValidResponse = false;
          } else {
            hasFactor = codResponse === "yes";
            factorName = "Co-occurring Diagnosis";
          }
          break;
        case "mat":
          const matResponse = checkResponse(row.MAT || row.mat || row.isMAT);
          if (matResponse !== "yes" && matResponse !== "no") {
            hasValidResponse = false;
          } else {
            hasFactor = matResponse === "yes";
            factorName = "MAT Treatment";
          }
          break;
        case "purpose":
          const purposeDirect =
            checkResponse(row.purpose || row["Purpose"]) === "yes";
          const purposeScore =
            +row.lifeHasPurpose === 3 || +row.lifeHasPurpose === 4;
          hasFactor = purposeDirect || purposeScore;
          factorName = "Sense of Purpose";
          // Purpose uses numeric scale, so no validation needed
          break;
        case "prison":
          const prisonResponse = checkResponse(row.overOneYearInPrison);
          if (prisonResponse !== "yes" && prisonResponse !== "no") {
            hasValidResponse = false;
          } else {
            hasFactor = prisonResponse === "yes";
            factorName = "Prison Experience";
          }
          break;
        case "housing":
          const housingResponse = checkResponse(row.homelessOneYear);
          if (housingResponse !== "yes" && housingResponse !== "no") {
            hasValidResponse = false;
          } else {
            hasFactor = housingResponse === "yes";
            factorName = "Chronic Homelessness";
          }
          break;
      }

      // Only apply factor if response is valid
      if (hasValidResponse && hasFactor && weight > 0) {
        riskScore += weight;
        riskFactors.push(factorName);
        factorContributions[factorName] = weight;
      }
    });

    // RC Score (protective factor) - use standardized z-score
    const rcTotal = +row.recoveryCapitalTotal || 0;
    const rcScores = rowsForStats.map((r) => +r.recoveryCapitalTotal || 0);
    const rcMean =
      rcScores.reduce((sum, val) => sum + val, 0) / rcScores.length;
    const rcSD = Math.sqrt(
      rcScores.reduce((sum, val) => sum + Math.pow(val - rcMean, 2), 0) /
        rcScores.length
    );

    let rcContribution = 0;
    if (rcSD > 0) {
      const rcZScore = (rcTotal - rcMean) / rcSD;
      // Use correlation strength to weight RC impact
      const rcCorrelation =
        calculateCorrelation(
          rcScores,
          rowsForStats.map((r) => {
            const residentName =
              r.residentName ||
              r.fullName ||
              `${r.firstName} ${r.lastName}`.trim();
            const alum = alumniMap.get(residentName) || {};
            return isRelapse(alum.sobrietyStatus) ? 0 : 1;
          })
        ) || 0;

      rcContribution = -rcZScore * Math.abs(rcCorrelation); // Negative because higher RC = lower risk
      riskScore += rcContribution;
    }

    // Age adjustment (if empirically supported)
    const age = +row.age || +row.clientAge || 0;
    const ageScores = rowsForStats
      .map((r) => +r.age || +r.clientAge || 0)
      .filter((a) => a > 0);
    const avgAge =
      ageScores.reduce((sum, val) => sum + val, 0) / ageScores.length;

    let ageContribution = 0;
    if (age < avgAge - 10) {
      // Only significant age differences
      ageContribution = 0.5; // Modest contribution
      riskScore += ageContribution;
    }

    return {
      residentId: row.residentId,
      residentName,
      riskScore: +riskScore.toFixed(2),
      riskFactors,
      factorContributions, // NEW: Show contribution of each factor
      rcContribution: +rcContribution.toFixed(2),
      ageContribution: +ageContribution.toFixed(2),
      rcTotal,
      actualRelapse: relapsed,
      age,
      sourceCollection: row.sourceCollection, // 🔍 ADD: Track source for debugging
    };
  });

  // 🔍 DEBUG: Check risk score distribution by source
  const riskScoresBySource = riskScores.reduce((acc, resident) => {
    const source = resident.sourceCollection || "undefined";
    if (!acc[source]) acc[source] = [];
    acc[source].push(resident);
    return acc;
  }, {});

  Object.entries(riskScoresBySource).forEach(([source, residents]) => {});

  // 3️⃣ Use data-driven thresholds (not arbitrary percentiles)
  const scores = riskScores.map((r) => r.riskScore);

  // Use fixed, pre-specified thresholds instead of optimizing on training data
  const calculateValidatedThresholds = (riskScores) => {
    const scores = riskScores.map((r) => r.riskScore);
    const q33 = calculatePercentile(scores, 33);
    const q67 = calculatePercentile(scores, 67);

    return {
      medium: q33,
      high: q67,
      method: "Fixed percentile thresholds (33rd/67th percentiles)",
    };
  };

  const validatedThresholds = calculateValidatedThresholds(riskScores);

  // Apply pre-specified thresholds (no data snooping)
  riskScores.forEach((r) => {
    r.riskLevel =
      r.riskScore >= validatedThresholds.high
        ? "high"
        : r.riskScore >= validatedThresholds.medium
        ? "medium"
        : "low";
  });

  // 🔍 DEBUG: Final risk distribution by source
  const finalDistribution = riskScores.reduce((acc, resident) => {
    const source = resident.sourceCollection || "undefined";
    const level = resident.riskLevel;

    if (!acc[source]) acc[source] = { low: 0, medium: 0, high: 0, total: 0 };
    acc[source][level]++;
    acc[source].total++;
    return acc;
  }, {});

  Object.entries(finalDistribution).forEach(([source, dist]) => {});

  // 4️⃣ Calculate validated accuracy metrics
  const riskAccuracy = {
    lowRisk:
      riskScores.filter((r) => r.riskLevel === "low" && !r.actualRelapse)
        .length /
      Math.max(1, riskScores.filter((r) => r.riskLevel === "low").length),
    mediumRisk:
      riskScores.filter((r) => r.riskLevel === "medium" && !r.actualRelapse)
        .length /
      Math.max(1, riskScores.filter((r) => r.riskLevel === "medium").length),
    highRisk:
      riskScores.filter((r) => r.riskLevel === "high" && r.actualRelapse)
        .length /
      Math.max(1, riskScores.filter((r) => r.riskLevel === "high").length),
  };

  return {
    riskDistribution: {
      low: riskScores.filter((r) => r.riskLevel === "low").length,
      medium: riskScores.filter((r) => r.riskLevel === "medium").length,
      high: riskScores.filter((r) => r.riskLevel === "high").length,
    },
    riskAccuracy,
    riskScores,
    empiricalWeights, // NEW: Show the weights used
    thresholds: validatedThresholds, // Use the fixed thresholds
    modelValidation: {
      overallAccuracy: +(
        (riskScores.filter((r) => (r.riskLevel === "high") === r.actualRelapse)
          .length /
          riskScores.length) *
        100
      ).toFixed(1),
      weightsUsed: empiricalWeights,
      thresholdOptimization: validatedThresholds.method, // Use the proper method description
    },
  };
};

/**
 * DYNAMIC SUCCESS PREDICTION MODEL
 */
const calculateSuccessPredictionModelScientific = (
  rowsForStats,
  alumniMap,
  riskMetrics
) => {
  // Calculate feature importance from correlations
  const rcScores = rowsForStats.map((r) => +r.recoveryCapitalTotal || 0);
  const successOutcomes = rowsForStats.map((r) => {
    const alumni = alumniMap.get(r.residentId) || {};
    return isRelapse(alumni.sobrietyStatus) ? 0 : 1;
  });

  const rcSuccessCorrelation =
    calculateCorrelation(rcScores, successOutcomes) || 0;

  // Age correlation
  const ageScores = rowsForStats
    .map((r) => +r.age || +r.clientAge || 0)
    .filter((a) => a > 0);
  const ageOutcomes = rowsForStats
    .filter((r) => (+r.age || +r.clientAge || 0) > 0)
    .map((r) => {
      const alumni = alumniMap.get(r.residentId) || {};
      return isRelapse(alumni.sobrietyStatus) ? 0 : 1;
    });
  const ageSuccessCorrelation =
    calculateCorrelation(ageScores, ageOutcomes) || 0;

  // FIXED: Calculate baseline success rate OUTSIDE the map function
  const baselineSuccessRate =
    successOutcomes.filter((o) => o === 1).length / successOutcomes.length;

  // Calculate RC statistics once (not inside the loop)
  const rcMean = rcScores.reduce((sum, val) => sum + val, 0) / rcScores.length;
  const rcSD = Math.sqrt(
    rcScores.reduce((sum, val) => sum + Math.pow(val - rcMean, 2), 0) /
      rcScores.length
  );

  // Calculate age statistics once
  const avgAge =
    ageScores.length > 0
      ? ageScores.reduce((sum, val) => sum + val, 0) / ageScores.length
      : 0;
  const ageSD =
    ageScores.length > 0
      ? Math.sqrt(
          ageScores.reduce((sum, val) => sum + Math.pow(val - avgAge, 2), 0) /
            ageScores.length
        )
      : 1;

  const predictions = rowsForStats.map((row) => {
    const residentName =
      row.residentName ||
      row.fullName ||
      `${row.firstName} ${row.lastName}`.trim();
    const alumni = alumniMap.get(residentName) || {};
    const actualSuccess = !isRelapse(alumni.sobrietyStatus);

    // Start with baseline success rate
    let successScore = baselineSuccessRate * 100; // Convert to percentage

    // RC impact (use actual correlation strength, not arbitrary multiplier)
    const rcTotal = +row.recoveryCapitalTotal || 0;

    if (rcSD > 0) {
      const rcZScore = (rcTotal - rcMean) / rcSD;
      // Use correlation to determine impact magnitude
      const rcImpact = rcZScore * Math.abs(rcSuccessCorrelation) * 20; // 20% max impact
      successScore += rcImpact;
    }

    // Apply factor impacts based on empirically-derived odds ratios
    Object.entries(riskMetrics).forEach(([factor, metric]) => {
      if (!metric) return;

      let hasFactor = false;
      switch (factor) {
        case "trauma":
          hasFactor = checkResponse(row.childhoodTrauma) === "yes";
          break;
        case "cod":
          hasFactor = checkResponse(row.coOccurringDiagnosis) === "yes";
          break;
        case "prison":
          hasFactor = checkResponse(row.overOneYearInPrison) === "yes";
          break;
        case "housing":
          hasFactor = checkResponse(row.homelessOneYear) === "yes";
          break;
        case "purpose":
          const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
            .map((f) => +row[f])
            .filter((v) => !Number.isNaN(v));
          hasFactor =
            purposeItems.length &&
            purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3;
          break;
        case "mat":
          hasFactor = checkResponse(row.MAT || row.mat || row.isMAT) === "yes";
          break;
        case "twoParentHome":
          hasFactor = checkResponse(row.twoParentsHome) === "yes";
          break;
        case "guilt":
          hasFactor = hasGuiltShame(row.guiltOrShame);
          break;
      }

      if (hasFactor) {
        // Convert OR to probability impact using log-odds
        const logOR = Math.log(metric.or);
        const confidenceWeight =
          metric.strength === "high"
            ? 1.0
            : metric.strength === "moderate"
            ? 0.7
            : 0.4;

        const probabilityImpact = logOR * confidenceWeight * 10; // Scale to percentage points

        if (metric.or > 1) {
          successScore -= Math.abs(probabilityImpact); // Risk factor decreases success
        } else {
          successScore += Math.abs(probabilityImpact); // Protective factor increases success
        }
      }
    });

    // Age factor (use actual correlation, not arbitrary adjustment)
    const age = +row.age || +row.clientAge || 0;
    if (age > 0 && Math.abs(ageSuccessCorrelation) > 0.1 && ageSD > 0) {
      // Only if correlation is meaningful
      const ageZScore = (age - avgAge) / ageSD;
      successScore += ageZScore * ageSuccessCorrelation * 10; // 10% max age impact
    }

    // Bound between 5% and 95% (more realistic than 0-100)
    successScore = Math.max(5, Math.min(95, successScore));

    const predictedSuccess = successScore >= 50;
    const confidence = Math.abs(successScore - 50) / 45; // Adjusted for 5-95 range

    return {
      residentId: row.residentId,
      residentName,
      successScore: +successScore.toFixed(1),
      predictedSuccess,
      actualSuccess,
      confidence: +confidence.toFixed(2),
      correct: predictedSuccess === actualSuccess,
    };
  });

  // REPLACE THE PROBLEMATIC ACCURACY CALCULATION:
  const calculateValidatedAccuracy = (predictions) => {
    if (predictions.length < 20) {
      return {
        accuracy: null,
        warning: "Sample too small for reliable train/test validation",
        totalSample: predictions.length,
        method: "Insufficient data for proper validation",
      };
    }

    // Use temporal split if possible, otherwise random 80/20
    const shuffled = [...predictions].sort(() => Math.random() - 0.5);
    const splitIndex = Math.floor(predictions.length * 0.8);
    const trainSet = shuffled.slice(0, splitIndex);
    const testSet = shuffled.slice(splitIndex);

    if (testSet.length < 5) {
      return {
        accuracy: +(
          (predictions.filter((p) => p.correct).length / predictions.length) *
          100
        ).toFixed(1),
        warning:
          "Using full dataset - insufficient for proper validation split",
        totalSample: predictions.length,
        method: "Full dataset (no validation split possible)",
      };
    }

    const testAccuracy =
      testSet.filter((p) => p.correct).length / testSet.length;

    return {
      accuracy: +(testAccuracy * 100).toFixed(1),
      trainSize: trainSet.length,
      testSize: testSet.length,
      method: "80/20 train/test split",
      warning:
        testSet.length < 10
          ? "Small test set - results may be unreliable"
          : null,
    };
  };

  const validatedResults = calculateValidatedAccuracy(predictions);

  // Calculate calibration (how well predicted probabilities match actual rates)
  const calibrationBins = [];
  for (let i = 0; i < 100; i += 10) {
    const binPredictions = predictions.filter(
      (p) => p.successScore >= i && p.successScore < i + 10
    );
    if (binPredictions.length > 0) {
      const actualSuccessRate =
        binPredictions.filter((p) => p.actualSuccess).length /
        binPredictions.length;
      const predictedRate =
        binPredictions.reduce((sum, p) => sum + p.successScore, 0) /
        binPredictions.length /
        100;
      calibrationBins.push({
        range: `${i}-${i + 10}%`,
        predicted: +(predictedRate * 100).toFixed(1),
        actual: +(actualSuccessRate * 100).toFixed(1),
        count: binPredictions.length,
      });
    }
  }

  return {
    modelAccuracy: validatedResults.accuracy,
    predictions,
    successDistribution: {
      highSuccess: predictions.filter((p) => p.successScore >= 75).length,
      moderateSuccess: predictions.filter(
        (p) => p.successScore >= 50 && p.successScore < 75
      ).length,
      lowSuccess: predictions.filter((p) => p.successScore < 50).length,
    },
    featureImportance: {
      rcCorrelation: +rcSuccessCorrelation.toFixed(3),
      ageCorrelation: +ageSuccessCorrelation.toFixed(3),
      baselineSuccessRate: +(baselineSuccessRate * 100).toFixed(1),
    },
    modelValidation: {
      calibration: calibrationBins,
      methodology: validatedResults.method,
      validationWarning: validatedResults.warning,
      trainSize: validatedResults.trainSize,
      testSize: validatedResults.testSize,
      confidenceAdjusted: true,
      boundsApplied: "5-95% realistic probability range",
    },
  };
};
/**
 * DYNAMIC PROGRAM QUALITY SCORING - Calculate thresholds from your data
 */
const calculateProgramQualityScore = (metrics) => {
  let qualityScore = 0;
  let maxPossible = 0;

  // RC Performance (25 points max) - based on your data percentiles
  const rcPercentile =
    metrics.benchmarkComparisons.yourPerformance.percentileRanking;
  if (metrics.rcData.rcMean >= rcPercentile.p90) qualityScore += 25;
  else if (metrics.rcData.rcMean >= rcPercentile.p75) qualityScore += 20;
  else if (metrics.rcData.rcMean >= rcPercentile.p50) qualityScore += 15;
  else if (metrics.rcData.rcMean >= rcPercentile.p25) qualityScore += 10;
  maxPossible += 25;

  // Risk Management (20 points max) - based on actual risk accuracy
  const highRiskManaged =
    metrics.riskStratification?.riskAccuracy?.highRisk || 0;
  if (highRiskManaged >= 0.8) qualityScore += 20;
  else if (highRiskManaged >= 0.6) qualityScore += 15;
  else if (highRiskManaged >= 0.4) qualityScore += 10;
  else if (highRiskManaged >= 0.2) qualityScore += 5;
  maxPossible += 20;

  // Model Accuracy (15 points max) - based on prediction model performance
  const modelAccuracy = metrics.successPredictionModel?.modelAccuracy || 0;
  if (modelAccuracy >= 80) qualityScore += 15;
  else if (modelAccuracy >= 70) qualityScore += 12;
  else if (modelAccuracy >= 60) qualityScore += 8;
  else if (modelAccuracy >= 50) qualityScore += 5;
  maxPossible += 15;

  // Data Quality (15 points max) - based on sample size and completeness
  const dataCompleteness =
    metrics.rcData.assessmentCount /
    Math.max(1, metrics.rcData.totalUniqueIndividuals);
  if (dataCompleteness >= 3) qualityScore += 15;
  else if (dataCompleteness >= 2) qualityScore += 12;
  else if (dataCompleteness >= 1.5) qualityScore += 8;
  else if (dataCompleteness >= 1) qualityScore += 5;
  maxPossible += 15;

  // Sample Size (10 points max)
  if (metrics.rcData.totalUniqueIndividuals >= 100) qualityScore += 10;
  else if (metrics.rcData.totalUniqueIndividuals >= 50) qualityScore += 8;
  else if (metrics.rcData.totalUniqueIndividuals >= 25) qualityScore += 6;
  else if (metrics.rcData.totalUniqueIndividuals >= 10) qualityScore += 3;
  maxPossible += 10;

  // Factor Significance (10 points max) - how many factors show strong relationships
  const significantFactors = Object.values(metrics.riskMetrics).filter(
    (metric) =>
      metric &&
      (metric.or > 1.5 || metric.or < 0.67) &&
      metric.strength !== "low"
  ).length;
  if (significantFactors >= 5) qualityScore += 10;
  else if (significantFactors >= 3) qualityScore += 7;
  else if (significantFactors >= 2) qualityScore += 5;
  else if (significantFactors >= 1) qualityScore += 2;
  maxPossible += 10;

  // Domain Balance (5 points max) - how balanced are the domain scores
  const domainScores = metrics.domainData.map((d) => d.score);
  const domainMean =
    domainScores.reduce((sum, val) => sum + val, 0) / domainScores.length;
  const domainVariance =
    domainScores.reduce((sum, val) => sum + Math.pow(val - domainMean, 2), 0) /
    domainScores.length;
  const domainCV = domainMean > 0 ? Math.sqrt(domainVariance) / domainMean : 1; // Coefficient of variation

  if (domainCV <= 0.1) qualityScore += 5;
  else if (domainCV <= 0.2) qualityScore += 3;
  else if (domainCV <= 0.3) qualityScore += 1;
  maxPossible += 5;

  const finalScore = (qualityScore / maxPossible) * 100;

  return {
    totalScore: +finalScore.toFixed(1),
    breakdown: {
      rcPerformance: qualityScore >= 25 ? 25 : qualityScore,
      riskManagement: Math.max(0, Math.min(20, qualityScore - 25)),
      modelAccuracy: Math.max(0, Math.min(15, qualityScore - 45)),
      dataQuality: Math.max(0, Math.min(15, qualityScore - 60)),
      sampleSize: Math.max(0, Math.min(10, qualityScore - 75)),
      factorSignificance: Math.max(0, Math.min(10, qualityScore - 85)),
      domainBalance: Math.max(0, Math.min(5, qualityScore - 95)),
    },
    rating:
      finalScore >= 85
        ? "Excellent"
        : finalScore >= 70
        ? "Good"
        : finalScore >= 55
        ? "Average"
        : "Needs Improvement",
    recommendations: generateRecommendations(finalScore, metrics),
    dynamicThresholds: {
      excellent: 85,
      good: 70,
      average: 55,
      significantFactors,
      domainBalance: domainCV,
    },
  };
};

const generateRecommendations = (score, metrics) => {
  const recommendations = [];

  // RC Performance recommendations
  const rcPercentile =
    metrics.benchmarkComparisons.yourPerformance.percentileRanking;
  if (metrics.rcData.rcMean < rcPercentile.p75) {
    recommendations.push({
      category: "RC Improvement",
      priority: "High",
      suggestion: `Focus on top-performing domains: ${metrics.domainData
        .slice(0, 2)
        .map((d) => d.name)
        .join(" and ")}. Current average: ${metrics.rcData.rcMean}`,
    });
  }

  // Risk management recommendations
  if (metrics.riskStratification?.riskAccuracy?.highRisk < 0.6) {
    const topRiskFactors = Object.entries(metrics.riskMetrics)
      .filter(([_, metric]) => metric && metric.or > 2)
      .sort((a, b) => b[1].or - a[1].or)
      .slice(0, 2)
      .map(([factor, _]) => factor);

    recommendations.push({
      category: "Risk Management",
      priority: "High",
      suggestion: `Implement targeted interventions for: ${topRiskFactors.join(
        ", "
      )}. Current high-risk accuracy: ${(
        metrics.riskStratification.riskAccuracy.highRisk * 100
      ).toFixed(1)}%`,
    });
  }

  // Model accuracy recommendations
  if (metrics.successPredictionModel?.modelAccuracy < 70) {
    recommendations.push({
      category: "Predictive Analytics",
      priority: "Medium",
      suggestion: `Improve prediction model by collecting more data on factors with strong correlations. Current accuracy: ${metrics.successPredictionModel.modelAccuracy}%`,
    });
  }

  // Data quality recommendations
  const dataCompleteness =
    metrics.rcData.assessmentCount / metrics.rcData.totalUniqueIndividuals;
  if (dataCompleteness < 2) {
    recommendations.push({
      category: "Data Collection",
      priority: "Medium",
      suggestion: `Increase assessment frequency. Current: ${dataCompleteness.toFixed(
        1
      )} assessments per resident. Target: 2+ assessments per resident`,
    });
  }

  // Sample size recommendations
  if (metrics.rcData.totalUniqueIndividuals < 50) {
    recommendations.push({
      category: "Sample Size",
      priority: "Low",
      suggestion: `Continue data collection to reach statistical significance. Current: ${metrics.rcData.totalUniqueIndividuals} residents. Target: 50+ residents`,
    });
  }

  return recommendations.slice(0, 5); // Top 5 recommendations
};

const buildFactorRankings = (riskMetrics) =>
  Object.entries(riskMetrics)
    .filter(([_, metric]) => metric && metric.rcDiff !== undefined)
    .map(([factor, metric]) => ({
      factor:
        factor.charAt(0).toUpperCase() +
        factor.slice(1).replace(/([A-Z])/g, " $1"),
      rcDifference: metric.rcDiff,
      oddsRatio: metric.or,
      strength: metric.strength,
      color: getFactorColor(factor), // Dynamic color assignment
    }))
    .sort((a, b) => Math.abs(b.rcDifference) - Math.abs(a.rcDifference));

const getFactorColor = (factor) => {
  const colorMap = {
    trauma: "bg-red-500",
    cod: "bg-red-400",
    prison: "bg-amber-500",
    housing: "bg-orange-500",
    purpose: "bg-blue-500",
    twoParentHome: "bg-emerald-500",
    guilt: "bg-purple-500",
    therapy: "bg-green-500",
    strongKidsRelation: "bg-pink-500",
  };
  return colorMap[factor] || "bg-gray-500";
};

/**
 * ENHANCED PROGRESSION TRACKING
 */
const calculateProgressionMetrics = (rawData) => {
  const progressionByResident = new Map();

  // Group assessments by resident
  rawData.forEach((row) => {
    const uid = row.residentId;
    if (!progressionByResident.has(uid)) {
      progressionByResident.set(uid, []);
    }
    progressionByResident.get(uid).push({
      ...row,
      timestamp: row.timestamp?.toDate?.() ?? new Date(row.timestamp),
    });
  });

  const progressionStats = Array.from(progressionByResident.values())
    .filter((assessments) => assessments.length >= 2)
    .map((assessments) => {
      // Sort by timestamp
      assessments.sort((a, b) => a.timestamp - b.timestamp);

      const first = assessments[0];
      const last = assessments[assessments.length - 1];
      const daysBetween =
        (last.timestamp - first.timestamp) / (1000 * 60 * 60 * 24);

      const rcImprovement =
        (+last.recoveryCapitalTotal || 0) - (+first.recoveryCapitalTotal || 0);
      const improvementRate =
        daysBetween > 0 ? (rcImprovement / daysBetween) * 30 : 0; // per month

      // Calculate domain improvements
      const domains = [
        "spiritual",
        "social",
        "individual",
        "physical",
        "general",
      ];
      const domainImprovements = domains.map((domain) => ({
        domain,
        improvement:
          (+last[`${domain}CapitalScore`] || 0) -
          (+first[`${domain}CapitalScore`] || 0),
      }));

      return {
        residentId: first.residentId,
        assessmentCount: assessments.length,
        daysBetween,
        initialRC: +first.recoveryCapitalTotal || 0,
        finalRC: +last.recoveryCapitalTotal || 0,
        rcImprovement,
        improvementRate,
        domainImprovements,
        achievedMilestones: {
          reached100: (+last.recoveryCapitalTotal || 0) >= 100,
          reached120: (+last.recoveryCapitalTotal || 0) >= 120,
          improved20Points: rcImprovement >= 20,
          improved30Points: rcImprovement >= 30,
        },
      };
    });

  // Calculate dynamic milestones based on data distribution
  const allFinalRCs = progressionStats.map((p) => p.finalRC);
  const dynamicMilestones = {
    milestone75th: calculatePercentile(allFinalRCs, 75),
    milestone90th: calculatePercentile(allFinalRCs, 90),
    avgImprovement:
      progressionStats.reduce((sum, p) => sum + p.rcImprovement, 0) /
      Math.max(1, progressionStats.length),
  };

  return {
    averageImprovementRate:
      progressionStats.reduce((sum, p) => sum + p.improvementRate, 0) /
      Math.max(1, progressionStats.length),
    milestoneAchievement: {
      reached100Percent:
        (progressionStats.filter((p) => p.achievedMilestones.reached100)
          .length /
          Math.max(1, progressionStats.length)) *
        100,
      reached120Percent:
        (progressionStats.filter((p) => p.achievedMilestones.reached120)
          .length /
          Math.max(1, progressionStats.length)) *
        100,
      improved20Percent:
        (progressionStats.filter((p) => p.achievedMilestones.improved20Points)
          .length /
          Math.max(1, progressionStats.length)) *
        100,
      improved30Percent:
        (progressionStats.filter((p) => p.achievedMilestones.improved30Points)
          .length /
          Math.max(1, progressionStats.length)) *
        100,
    },
    progressionStats,
    dynamicMilestones,
    domainProgressionAnalysis: calculateDomainProgression(progressionStats),
  };
};

const calculateDomainProgression = (progressionStats) => {
  const domains = ["spiritual", "social", "individual", "physical", "general"];

  return domains.map((domain) => {
    const improvements = progressionStats
      .flatMap((p) => p.domainImprovements)
      .filter((d) => d.domain === domain)
      .map((d) => d.improvement);

    const avgImprovement =
      improvements.length > 0
        ? improvements.reduce((sum, val) => sum + val, 0) / improvements.length
        : 0;

    return {
      domain: domain.charAt(0).toUpperCase() + domain.slice(1),
      averageImprovement: +avgImprovement.toFixed(1),
      improvementCount: improvements.filter((i) => i > 0).length,
      totalAssessments: improvements.length,
    };
  });
};

/**
 * ENHANCED LENGTH OF STAY ANALYSIS
 */
const calculateLengthOfStayAnalysis = (rawData, alumniMap) => {
  const losAnalysis = new Map();

  rawData.forEach((row) => {
    const uid = row.residentId;
    const alumni = alumniMap.get(uid) || {};

    if (!losAnalysis.has(uid)) {
      // Handle different possible date field names
      const intakeDate = new Date(
        row.intakeDate || row.admissionDate || "2023-01-01"
      );
      const dischargeDate = alumni.dischargeDate
        ? new Date(alumni.dischargeDate)
        : new Date();
      const lengthOfStay = Math.max(
        1,
        (dischargeDate - intakeDate) / (1000 * 60 * 60 * 24)
      );

      losAnalysis.set(uid, {
        residentId: uid,
        lengthOfStay,
        rcTotal: +row.recoveryCapitalTotal || 0,
        relapsed: isRelapse(alumni.sobrietyStatus),
        age: +row.age || +row.clientAge || 0,
      });
    }
  });

  const losData = Array.from(losAnalysis.values());

  // Calculate dynamic LOS buckets based on data distribution
  const allLOS = losData.map((r) => r.lengthOfStay);
  const los33rd = calculatePercentile(allLOS, 33);
  const los67th = calculatePercentile(allLOS, 67);

  const losBuckets = {
    short: { residents: [], label: `0-${Math.round(los33rd)} days` },
    medium: {
      residents: [],
      label: `${Math.round(los33rd + 1)}-${Math.round(los67th)} days`,
    },
    long: { residents: [], label: `${Math.round(los67th + 1)}+ days` },
  };

  losData.forEach((resident) => {
    if (resident.lengthOfStay <= los33rd)
      losBuckets.short.residents.push(resident);
    else if (resident.lengthOfStay <= los67th)
      losBuckets.medium.residents.push(resident);
    else losBuckets.long.residents.push(resident);
  });

  const calculateBucketStats = (bucket) => {
    if (bucket.residents.length === 0) return null;

    return {
      count: bucket.residents.length,
      averageRC: +(
        bucket.residents.reduce((sum, r) => sum + r.rcTotal, 0) /
        bucket.residents.length
      ).toFixed(1),
      relapseRate: +(
        (bucket.residents.filter((r) => r.relapsed).length /
          bucket.residents.length) *
        100
      ).toFixed(1),
      averageLOS: +(
        bucket.residents.reduce((sum, r) => sum + r.lengthOfStay, 0) /
        bucket.residents.length
      ).toFixed(0),
    };
  };

  // Calculate optimal LOS range (where outcomes are best)
  const losOutcomeAnalysis = losData.map((r) => ({
    los: r.lengthOfStay,
    outcome: r.relapsed ? 0 : 1, // 1 = success, 0 = relapse
    rc: r.rcTotal,
  }));

  return {
    bucketAnalysis: {
      short: {
        ...calculateBucketStats(losBuckets.short),
        label: losBuckets.short.label,
      },
      medium: {
        ...calculateBucketStats(losBuckets.medium),
        label: losBuckets.medium.label,
      },
      long: {
        ...calculateBucketStats(losBuckets.long),
        label: losBuckets.long.label,
      },
    },
    optimalLOS: calculateOptimalLOS(losOutcomeAnalysis),
    correlation: calculateCorrelation(
      losData.map((r) => r.lengthOfStay),
      losData.map((r) => r.rcTotal)
    ),
    dynamicThresholds: { los33rd, los67th },
  };
};

const calculateOptimalLOS = (losOutcomeAnalysis) => {
  // Find LOS range with highest success rate
  const losBands = [
    { min: 0, max: 60, name: "0-60 days" },
    { min: 61, max: 120, name: "61-120 days" },
    { min: 121, max: 180, name: "121-180 days" },
    { min: 181, max: 365, name: "181-365 days" },
    { min: 366, max: Infinity, name: "365+ days" },
  ];

  const bandAnalysis = losBands.map((band) => {
    const inBand = losOutcomeAnalysis.filter(
      (r) => r.los >= band.min && r.los <= band.max
    );
    const successRate =
      inBand.length > 0
        ? inBand.filter((r) => r.outcome === 1).length / inBand.length
        : 0;

    return {
      ...band,
      count: inBand.length,
      successRate: +(successRate * 100).toFixed(1),
      averageRC:
        inBand.length > 0
          ? +(inBand.reduce((sum, r) => sum + r.rc, 0) / inBand.length).toFixed(
              1
            )
          : 0,
    };
  });

  const optimalBand = bandAnalysis
    .filter((b) => b.count >= 5) // Minimum sample size
    .sort((a, b) => b.successRate - a.successRate)[0];

  return optimalBand || { name: "Insufficient data", successRate: 0 };
};

/**
 * INTERVENTION EFFECTIVENESS TRACKING
 */
const calculateInterventionEffectiveness = (rawData) => {
  const interventions = {
    therapy: {
      name: "Therapy Participation",
      withIntervention: [],
      withoutIntervention: [],
      check: (row) => isInTherapy(row.therapy),
    },
    guiltShameProcessing: {
      name: "Has Guilt/Shame",
      withIntervention: [],
      withoutIntervention: [],
      check: (row) => hasGuiltShame(row.guiltOrShame),
    },
    // Add more interventions as needed
  };

  rawData.forEach((row) => {
    const rcTotal = +row.recoveryCapitalTotal || 0;

    Object.values(interventions).forEach((intervention) => {
      if (intervention.check(row)) {
        intervention.withIntervention.push(rcTotal);
      } else {
        intervention.withoutIntervention.push(rcTotal);
      }
    });
  });

  const interventionAnalysis = {};

  Object.entries(interventions).forEach(([key, intervention]) => {
    const withMean =
      intervention.withIntervention.length > 0
        ? intervention.withIntervention.reduce((sum, val) => sum + val, 0) /
          intervention.withIntervention.length
        : 0;
    const withoutMean =
      intervention.withoutIntervention.length > 0
        ? intervention.withoutIntervention.reduce((sum, val) => sum + val, 0) /
          intervention.withoutIntervention.length
        : 0;

    const improvement = withMean - withoutMean;
    const effectSize = calculateEffectSize(
      intervention.withIntervention,
      intervention.withoutIntervention
    );

    interventionAnalysis[key] = {
      name: intervention.name,
      withIntervention: +withMean.toFixed(1),
      withoutIntervention: +withoutMean.toFixed(1),
      improvement: +improvement.toFixed(1),
      percentImprovement:
        withoutMean > 0 ? +((improvement / withoutMean) * 100).toFixed(1) : 0,
      effectSize: +effectSize.toFixed(2),
      sampleSizes: {
        with: intervention.withIntervention.length,
        without: intervention.withoutIntervention.length,
      },
    };
  });

  return interventionAnalysis;
};

// Add these new functions to your existing rcCalculations.js file

/**
 * CORE DATA FETCHING - Shared by all components
 */
const fetchCoreRCData = async (organizationId) => {
  if (!organizationId) throw new Error("Organization ID is required");

  // Fetch all raw data (same as your existing logic)
  const rcSnapshot = await getDocs(
    collection(db, `organizations/${organizationId}/recoveryCapital`)
  );
  const rawData = rcSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  const alumniSnapshot = await getDocs(
    collection(db, `organizations/${organizationId}/alumni`)
  );
  const alumniMap = new Map();
  alumniSnapshot.docs.forEach((d) => {
    const data = d.data();
    const fullName =
      data.fullName ||
      data.residentName ||
      `${data.firstName} ${data.lastName}`.trim();
    alumniMap.set(fullName, {
      ...data,
      originalId: d.id,
      sobrietyStatus: data.sobrietyStatus,
      dischargeDate: data.dischargeDate,
      sobrietyDate: data.sobrietyDate,
    });
  });

  const residentsSnapshot = await getDocs(
    collection(db, `organizations/${organizationId}/residents`)
  );
  const residentsMap = new Map();
  residentsSnapshot.docs.forEach((d) => {
    const data = d.data();
    const fullName =
      data.fullName ||
      data.residentName ||
      `${data.firstName} ${data.lastName}`.trim();
    residentsMap.set(fullName, {
      ...data,
      originalId: d.id,
      sobrietyDate: data.sobrietyDate,
      currentResident: data.currentResident,
      residentName: fullName,
    });
  });

  // Common data processing
  const historicalData = rawData.filter((row) => {
    const residentName = row.residentName;
    const alumni = alumniMap.get(residentName);
    return alumni && alumni.sobrietyStatus;
  });

  const currentResidentData = rawData.filter((row) => {
    const residentName = row.residentName;
    const resident = residentsMap.get(residentName);
    const alumni = alumniMap.get(residentName);
    return resident?.currentResident === true || !alumni?.sobrietyStatus;
  });

  const getLatestAssessmentPerResident = (data) => {
    const residentMap = new Map();
    data.forEach((row) => {
      const residentName =
        row.residentName ||
        row.fullName ||
        `${row.firstName} ${row.lastName}`.trim() ||
        row.residentId;
      const assessmentDate =
        row.timestamp?.toDate?.() || new Date(row.timestamp);
      if (
        !residentMap.has(residentName) ||
        assessmentDate >
          (residentMap.get(residentName).timestamp?.toDate?.() ||
            new Date(residentMap.get(residentName).timestamp))
      ) {
        residentMap.set(residentName, row);
      }
    });
    return Array.from(residentMap.values());
  };

  const deduplicatedHistoricalData =
    getLatestAssessmentPerResident(historicalData);
  const deduplicatedCurrentData =
    getLatestAssessmentPerResident(currentResidentData);
  const allData = [...deduplicatedHistoricalData, ...deduplicatedCurrentData];

  return {
    rawData,
    historicalData: deduplicatedHistoricalData,
    currentData: deduplicatedCurrentData,
    allData,
    alumniMap,
    residentsMap,
  };
};

/**
 * FOR RCPublicOverview.jsx - Basic metrics and domain analysis
 */
export const calculateOverviewMetrics = async (organizationId) => {
  try {
    const coreData = await fetchCoreRCData(organizationId);
    const { allData, alumniMap } = coreData;

    // Domain averages
    const domainAverages = [
      {
        name: "Spiritual Capital",
        score:
          allData.length > 0
            ? allData.reduce(
                (sum, row) => sum + (+row.spiritualCapitalScore || 0),
                0
              ) / allData.length
            : 0,
        color: "#8B5CF6",
      },
      {
        name: "Social Capital",
        score:
          allData.length > 0
            ? allData.reduce(
                (sum, row) => sum + (+row.socialCapitalScore || 0),
                0
              ) / allData.length
            : 0,
        color: "#3B82F6",
      },
      {
        name: "Individual Capital",
        score:
          allData.length > 0
            ? allData.reduce(
                (sum, row) => sum + (+row.individualCapitalScore || 0),
                0
              ) / allData.length
            : 0,
        color: "#10B981",
      },
      {
        name: "Physical Capital",
        score:
          allData.length > 0
            ? allData.reduce(
                (sum, row) => sum + (+row.physicalCapitalScore || 0),
                0
              ) / allData.length
            : 0,
        color: "#F59E0B",
      },
      {
        name: "General Capital",
        score:
          allData.length > 0
            ? allData.reduce(
                (sum, row) => sum + (+row.generalCapitalScore || 0),
                0
              ) / allData.length
            : 0,
        color: "#EC4899",
      },
    ]
      .map((d) => ({ ...d, score: +d.score.toFixed(1) }))
      .sort((a, b) => b.score - a.score);

    // ADD THIS FUNCTION HERE - OUTSIDE of buildBasicRiskMetrics
    const calculateDomainDistributions = (data) => {
      const domains = [
        {
          key: "spiritualCapitalScore",
          name: "Spiritual Capital",
          maxScore: 40,
        },
        { key: "socialCapitalScore", name: "Social Capital", maxScore: 40 },
        {
          key: "individualCapitalScore",
          name: "Individual Capital",
          maxScore: 40,
        },
        { key: "physicalCapitalScore", name: "Physical Capital", maxScore: 40 },
        { key: "generalCapitalScore", name: "General Capital", maxScore: 40 },
      ];

      return domains
        .map((domain) => {
          const scores = data
            .map((r) => +r[domain.key] || 0)
            .filter((s) => s > 0);
          if (scores.length === 0) return null;

          const sorted = scores.sort((a, b) => a - b);
          const min = Math.min(...scores);
          const max = Math.max(...scores);
          const mean = scores.reduce((s, v) => s + v, 0) / scores.length;
          const q25 = sorted[Math.floor(scores.length * 0.25)];
          const q75 = sorted[Math.floor(scores.length * 0.75)];
          const atMaxScore = scores.filter((s) => s >= max).length;
          const range = max - min;
          const scaleUtilization = (range / domain.maxScore) * 100;

          // Determine distribution quality and insights
          let distributionQuality = "excellent";
          let designInsights = [];

          // Check for ceiling effects (>30% scoring in top 10% of scale)
          const highScorePct =
            scores.filter((s) => s >= domain.maxScore * 0.9).length /
            scores.length;
          if (highScorePct > 0.3) {
            distributionQuality = "ceiling-bias";
            designInsights.push(
              "Slight ceiling bias - consider adding more challenging questions"
            );
          } else if (range >= domain.maxScore * 0.5) {
            designInsights.push("Excellent scale utilization");
          }

          // Check variance quality
          const variance =
            scores.reduce((sum, score) => sum + Math.pow(score - mean, 2), 0) /
            scores.length;
          const standardDev = Math.sqrt(variance);
          if (standardDev >= domain.maxScore * 0.15) {
            designInsights.push(
              "Good variance for detecting individual differences"
            );
          } else {
            designInsights.push("Limited variance - scores cluster together");
          }

          // Context-specific insights
          if (mean >= domain.maxScore * 0.8) {
            designInsights.push(
              "High scores reflect program effectiveness in this area"
            );
          } else if (mean <= domain.maxScore * 0.65) {
            designInsights.push(
              "Lower scores indicate significant growth opportunity"
            );
          }

          console.log(`🔧 ${domain.name}:`, {
            min,
            max,
            mean: mean.toFixed(1),
            distributionQuality,
          });

          return {
            domain: domain.name,
            key: domain.key,
            min,
            max,
            mean: +mean.toFixed(1),
            range,
            scaleUtilization: +scaleUtilization.toFixed(1),
            atMaxScore,
            totalResponses: scores.length,
            distributionQuality,
            standardDev: +standardDev.toFixed(1),
            designInsights,
          };
        })
        .filter(Boolean);
    };

    // CALCULATE DOMAIN DISTRIBUTIONS HERE
    const domainDistributions = calculateDomainDistributions(allData);
    console.log(
      "🔧 DEBUG: domainDistributions calculated:",
      domainDistributions
    );

    // Basic risk metrics from historical data only
    const buildBasicRiskMetrics = (data) => {
      console.log("🔍 DEBUG: Starting buildBasicRiskMetrics");
      console.log(`📊 Historical data sample size: ${data.length}`);

      const traumaBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const prisonBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const codBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const housingBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const guiltBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const purposeBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const cravingsBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const twoParentBucket = { with: { c: 0, r: 0 }, without: { c: 0, r: 0 } };
      const kidsRelationshipBucket = {
        with: { c: 0, r: 0 },
        without: { c: 0, r: 0 },
      };

      data.forEach((row) => {
        const residentName =
          row.residentName ||
          row.fullName ||
          `${row.firstName} ${row.lastName}`.trim();
        const alumni = alumniMap.get(residentName) || {};
        const relapsed = alumni.sobrietyStatus === "Not Sober";

        // Trauma
        if (checkResponse(row.childhoodTrauma) === "yes") {
          traumaBucket.with.c += 1;
          if (relapsed) traumaBucket.with.r += 1;
        } else if (checkResponse(row.childhoodTrauma) === "no") {
          traumaBucket.without.c += 1;
          if (relapsed) traumaBucket.without.r += 1;
        }

        // Prison
        if (checkResponse(row.overOneYearInPrison) === "yes") {
          prisonBucket.with.c += 1;
          if (relapsed) prisonBucket.with.r += 1;
        } else if (checkResponse(row.overOneYearInPrison) === "no") {
          prisonBucket.without.c += 1;
          if (relapsed) prisonBucket.without.r += 1;
        }

        // COD
        if (checkResponse(row.coOccurringDiagnosis) === "yes") {
          codBucket.with.c += 1;
          if (relapsed) codBucket.with.r += 1;
        } else if (checkResponse(row.coOccurringDiagnosis) === "no") {
          codBucket.without.c += 1;
          if (relapsed) codBucket.without.r += 1;
        }

        // Housing (Homeless ≥ 1 Year)
        if (checkResponse(row.homelessOneYear) === "yes") {
          housingBucket.with.c += 1;
          if (relapsed) housingBucket.with.r += 1;
        } else if (checkResponse(row.homelessOneYear) === "no") {
          housingBucket.without.c += 1;
          if (relapsed) housingBucket.without.r += 1;
        }

        // Guilt/Shame
        if (hasGuiltShame(row.guiltOrShame)) {
          guiltBucket.with.c += 1;
          if (relapsed) guiltBucket.with.r += 1;
        } else {
          guiltBucket.without.c += 1;
          if (relapsed) guiltBucket.without.r += 1;
        }

        // Purpose
        const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
          .map((f) => +row[f])
          .filter((v) => !Number.isNaN(v));
        const hasPurpose =
          purposeItems.length &&
          purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3;

        if (hasPurpose) {
          purposeBucket.with.c += 1;
          if (relapsed) purposeBucket.with.r += 1;
        } else {
          purposeBucket.without.c += 1;
          if (relapsed) purposeBucket.without.r += 1;
        }

        // Two Parent Home
        if (checkResponse(row.twoParentsHome) === "yes") {
          twoParentBucket.with.c += 1;
          if (relapsed) twoParentBucket.with.r += 1;
        } else if (checkResponse(row.twoParentsHome) === "no") {
          twoParentBucket.without.c += 1;
          if (relapsed) twoParentBucket.without.r += 1;
        }

        // Kids Relationship (only for those who have kids)
        if (checkResponse(row.kids) === "yes" && row.strongRelationKids) {
          const hasStrongRelation = hasStrongKidsRelation(
            row.strongRelationKids
          );
          if (hasStrongRelation) {
            kidsRelationshipBucket.with.c += 1;
            if (relapsed) kidsRelationshipBucket.with.r += 1;
          } else {
            kidsRelationshipBucket.without.c += 1;
            if (relapsed) kidsRelationshipBucket.without.r += 1;
          }
        }

        //Cravings
        if (
          row.cravings !== undefined &&
          row.cravings !== null &&
          row.cravings !== ""
        ) {
          if (hasActiveCravings(row.cravings)) {
            cravingsBucket.with.c += 1;
            if (relapsed) cravingsBucket.with.r += 1;
          } else {
            cravingsBucket.without.c += 1;
            if (relapsed) cravingsBucket.without.r += 1;
          }
        }
      });

      console.log("\n📊 FINAL BUCKET COUNTS:");
      console.log("COD Buckets:", {
        withCOD: codBucket.with,
        withoutCOD: codBucket.without,
        withCODRate:
          codBucket.with.c > 0
            ? ((codBucket.with.r / codBucket.with.c) * 100).toFixed(1)
            : "N/A",
        withoutCODRate:
          codBucket.without.c > 0
            ? ((codBucket.without.r / codBucket.without.c) * 100).toFixed(1)
            : "N/A",
      });

      return {
        trauma: buildMetric(traumaBucket.with, traumaBucket.without),
        prison: buildMetric(prisonBucket.with, prisonBucket.without),
        cod: buildMetric(codBucket.with, codBucket.without),
        housing: buildMetric(housingBucket.with, housingBucket.without),
        guilt: buildMetric(guiltBucket.with, guiltBucket.without),
        purpose: buildMetric(purposeBucket.with, purposeBucket.without),
        twoParentHome: buildMetric(
          twoParentBucket.with,
          twoParentBucket.without
        ),
        cravings:
          cravingsBucket.with.c > 0 || cravingsBucket.without.c > 0
            ? buildMetric(cravingsBucket.with, cravingsBucket.without)
            : null,
        kidsRelationship:
          kidsRelationshipBucket.with.c > 0 ||
          kidsRelationshipBucket.without.c > 0
            ? buildMetric(
                kidsRelationshipBucket.with,
                kidsRelationshipBucket.without
              )
            : null,
      };
    };

    // Basic correlations
    const validRows = allData.filter(
      (row) =>
        row.spiritualCapitalScore &&
        row.socialCapitalScore &&
        row.individualCapitalScore &&
        row.physicalCapitalScore &&
        row.generalCapitalScore
    );

    const correlations = {
      physicalToIndividual: calculateCorrelation(
        validRows.map((r) => +r.physicalCapitalScore),
        validRows.map((r) => +r.individualCapitalScore)
      ),
      socialToIndividual: calculateCorrelation(
        validRows.map((r) => +r.socialCapitalScore),
        validRows.map((r) => +r.individualCapitalScore)
      ),
      individualToSpiritual: calculateCorrelation(
        validRows.map((r) => +r.individualCapitalScore),
        validRows.map((r) => +r.spiritualCapitalScore)
      ),
      generalToPhysical: calculateCorrelation(
        validRows.map((r) => +r.generalCapitalScore),
        validRows.map((r) => +r.physicalCapitalScore)
      ),
    };

    const rcTotals = allData
      .filter((a) => a.recoveryCapitalTotal)
      .map((a) => +a.recoveryCapitalTotal);
    const rcMean = rcTotals.length
      ? rcTotals.reduce((s, v) => s + v, 0) / rcTotals.length
      : 0;
    const rcSD = rcTotals.length
      ? Math.sqrt(
          rcTotals.reduce((s, v) => s + Math.pow(v - rcMean, 2), 0) /
            rcTotals.length
        )
      : 0;

    console.log(
      "🔧 DEBUG: About to return with domainDistributions:",
      domainDistributions
    );

    return {
      domainData: domainAverages,
      rcData: {
        totalUniqueIndividuals: allData.length,
        rcMean: +rcMean.toFixed(1),
        rcSD: +rcSD.toFixed(1),
        correlations,
        assessmentCount: coreData.rawData.length,
        dataCollectionStart: "August 2023",
        dataCollectionEnd: new Date().toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        }),
      },
      riskMetrics: buildBasicRiskMetrics(coreData.historicalData),
      domainDistributions: domainDistributions, // NOW THIS WILL WORK
    };
  } catch (error) {
    console.error("Error in overview calculations:", error);
    throw error;
  }
};

/**
 * FOR RCKeyFactorsTab.jsx - Factor analysis and combinations
 */
export const calculateFactorAnalysis = async (organizationId) => {
  try {
    const coreData = await fetchCoreRCData(organizationId);
    const { allData, historicalData, alumniMap, rawData } = coreData;

    // Factor rankings
    const factorRankings = buildFactorRankings({
      trauma: buildMetric(
        {
          c: historicalData.filter(
            (r) => checkResponse(r.childhoodTrauma) === "yes"
          ).length,
          r: historicalData.filter(
            (r) =>
              checkResponse(r.childhoodTrauma) === "yes" &&
              isRelapse(alumniMap.get(r.residentName)?.sobrietyStatus)
          ).length,
        },
        {
          c: historicalData.filter(
            (r) => checkResponse(r.childhoodTrauma) === "no"
          ).length,
          r: historicalData.filter(
            (r) =>
              checkResponse(r.childhoodTrauma) === "no" &&
              isRelapse(alumniMap.get(r.residentName)?.sobrietyStatus)
          ).length,
        }
      ),
      // Add other factors as needed...
    });

    // Domain impacts
    const domainImpacts = createDomainImpacts(allData);

    // Factor combinations
    const weightSum = {};
    rawData.forEach((a) => {
      weightSum[byUID(a)] = (weightSum[byUID(a)] || 0) + 1;
    });
    const factorCombinations = calculateFactorCombinations(
      rawData,
      alumniMap,
      weightSum
    );

    // Build rawBuckets for compatibility
    const rawBuckets = {
      traumaRelapse: {
        withTrauma: { c: 0, r: 0, rc: 0 },
        withoutTrauma: { c: 0, r: 0, rc: 0 },
      },
      guiltRelapse: {
        guiltYes: { c: 0, r: 0, rc: 0 },
        guiltNo: { c: 0, r: 0, rc: 0 },
      },
      prisonRelapse: {
        prisonYes: { c: 0, r: 0, rc: 0 },
        prisonNo: { c: 0, r: 0, rc: 0 },
      },
      housingRelapse: {
        unstable: { c: 0, r: 0, rc: 0 },
        stable: { c: 0, r: 0, rc: 0 },
      },
      purposeRelapse: {
        withPurpose: { c: 0, r: 0, rc: 0 },
        withoutPurpose: { c: 0, r: 0, rc: 0 },
      },
      familySupportRC: {
        strong: { c: 0, r: 0, rc: 0 },
        weak: { c: 0, r: 0, rc: 0 },
      },
      kidsRelationship: {
        strong: { c: 0, r: 0, rc: 0 },
        weak: { c: 0, r: 0, rc: 0 },
      },
      codRelapse: {
        withCOD: { c: 0, r: 0, rc: 0 },
        withoutCOD: { c: 0, r: 0, rc: 0 },
      },
    };

    // Process historical data to build buckets
    historicalData.forEach((row) => {
      const residentName =
        row.residentName ||
        row.fullName ||
        `${row.firstName} ${row.lastName}`.trim();
      const alumni = alumniMap.get(residentName) || {};
      const relapsed = alumni.sobrietyStatus === "Not Sober";
      const rcTotal = +row.recoveryCapitalTotal || 0;

      // Trauma
      if (checkResponse(row.childhoodTrauma) === "yes") {
        rawBuckets.traumaRelapse.withTrauma.c += 1;
        rawBuckets.traumaRelapse.withTrauma.rc += rcTotal;
        if (relapsed) rawBuckets.traumaRelapse.withTrauma.r += 1;
      } else if (checkResponse(row.childhoodTrauma) === "no") {
        rawBuckets.traumaRelapse.withoutTrauma.c += 1;
        rawBuckets.traumaRelapse.withoutTrauma.rc += rcTotal;
        if (relapsed) rawBuckets.traumaRelapse.withoutTrauma.r += 1;
      }

      // Guilt/Shame
      if (hasGuiltShame(row.guiltOrShame)) {
        rawBuckets.guiltRelapse.guiltYes.c += 1;
        rawBuckets.guiltRelapse.guiltYes.rc += rcTotal;
        if (relapsed) rawBuckets.guiltRelapse.guiltYes.r += 1;
      } else {
        rawBuckets.guiltRelapse.guiltNo.c += 1;
        rawBuckets.guiltRelapse.guiltNo.rc += rcTotal;
        if (relapsed) rawBuckets.guiltRelapse.guiltNo.r += 1;
      }

      // Prison
      if (checkResponse(row.overOneYearInPrison) === "yes") {
        rawBuckets.prisonRelapse.prisonYes.c += 1;
        rawBuckets.prisonRelapse.prisonYes.rc += rcTotal;
        if (relapsed) rawBuckets.prisonRelapse.prisonYes.r += 1;
      } else if (checkResponse(row.overOneYearInPrison) === "no") {
        rawBuckets.prisonRelapse.prisonNo.c += 1;
        rawBuckets.prisonRelapse.prisonNo.rc += rcTotal;
        if (relapsed) rawBuckets.prisonRelapse.prisonNo.r += 1;
      }

      // Housing
      if (checkResponse(row.homelessOneYear) === "yes") {
        rawBuckets.housingRelapse.unstable.c += 1;
        rawBuckets.housingRelapse.unstable.rc += rcTotal;
        if (relapsed) rawBuckets.housingRelapse.unstable.r += 1;
      } else if (checkResponse(row.homelessOneYear) === "no") {
        rawBuckets.housingRelapse.stable.c += 1;
        rawBuckets.housingRelapse.stable.rc += rcTotal;
        if (relapsed) rawBuckets.housingRelapse.stable.r += 1;
      }

      // Purpose
      const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
        .map((f) => +row[f])
        .filter((v) => !Number.isNaN(v));
      const hasPurpose =
        purposeItems.length &&
        purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3;

      if (hasPurpose) {
        rawBuckets.purposeRelapse.withPurpose.c += 1;
        rawBuckets.purposeRelapse.withPurpose.rc += rcTotal;
        if (relapsed) rawBuckets.purposeRelapse.withPurpose.r += 1;
      } else {
        rawBuckets.purposeRelapse.withoutPurpose.c += 1;
        rawBuckets.purposeRelapse.withoutPurpose.rc += rcTotal;
        if (relapsed) rawBuckets.purposeRelapse.withoutPurpose.r += 1;
      }

      // Family Support
      if (checkResponse(row.twoParentsHome) === "yes") {
        rawBuckets.familySupportRC.strong.c += 1;
        rawBuckets.familySupportRC.strong.rc += rcTotal;
        if (relapsed) rawBuckets.familySupportRC.strong.r += 1;
      } else if (checkResponse(row.twoParentsHome) === "no") {
        rawBuckets.familySupportRC.weak.c += 1;
        rawBuckets.familySupportRC.weak.rc += rcTotal;
        if (relapsed) rawBuckets.familySupportRC.weak.r += 1;
      }

      // Kids Relationship
      if (checkResponse(row.kids) === "yes" && row.strongRelationKids) {
        const strong = [3, 4, "3", "4"].includes(row.strongRelationKids);
        const bucket = strong
          ? rawBuckets.kidsRelationship.strong
          : rawBuckets.kidsRelationship.weak;
        bucket.c += 1;
        bucket.rc += rcTotal;
        if (relapsed) bucket.r += 1;
      }

      // COD
      if (checkResponse(row.coOccurringDiagnosis) === "yes") {
        rawBuckets.codRelapse.withCOD.c += 1;
        rawBuckets.codRelapse.withCOD.rc += rcTotal;
        if (relapsed) rawBuckets.codRelapse.withCOD.r += 1;
      } else if (checkResponse(row.coOccurringDiagnosis) === "no") {
        rawBuckets.codRelapse.withoutCOD.c += 1;
        rawBuckets.codRelapse.withoutCOD.rc += rcTotal;
        if (relapsed) rawBuckets.codRelapse.withoutCOD.r += 1;
      }
    });

    return {
      factorRankings,
      domainImpacts,
      factorCombinations,
      rowsForStats: allData, // For filtering
      alumniMap, // For filtering logic
      rawBuckets, // Add this for compatibility
      rcData: {
        totalUniqueIndividuals: allData.length,
        assessmentCount: coreData.rawData.length,
        rcMean:
          allData.length > 0
            ? allData.reduce(
                (sum, row) => sum + (+row.recoveryCapitalTotal || 0),
                0
              ) / allData.length
            : 0,
      },
    };
  } catch (error) {
    console.error("Error in factor analysis:", error);
    throw error;
  }
};

/**
 * FOR RCMATAnalysisTab.jsx - Complete MAT-specific analysis
 */
export const calculateMATAnalysis = async (organizationId) => {
  try {
    const coreData = await fetchCoreRCData(organizationId);
    const { allData, alumniMap, residentsMap } = coreData;

    // Enhanced data with MAT status from residents/alumni collections - ALUMNI ONLY
    const combinedData = allData
      .filter((row) => {
        const residentName =
          row.residentName ||
          row.fullName ||
          `${row.firstName} ${row.lastName}`.trim();
        const alumni = alumniMap.get(residentName) || {};
        // Only include alumni with known sobriety outcomes
        return alumni.sobrietyStatus !== undefined;
      })
      .map((row) => {
        const residentName =
          row.residentName ||
          row.fullName ||
          `${row.firstName} ${row.lastName}`.trim();

        const resident = residentsMap.get(residentName) || {};
        const alumni = alumniMap.get(residentName) || {};

        // Get MAT status from either residents or alumni collection
        const isMAT = resident.isMAT === true || alumni.isMAT === true;

        // Get trauma status from recovery capital assessment
        const hasTrauma = checkResponse(row.childhoodTrauma) === "yes";

        // All of these are now alumni with known outcomes
        const isAlumni = true; // Always true now due to filter
        const sobrietyStatus = alumni.sobrietyStatus;

        return {
          ...row,
          isMAT,
          hasTrauma,
          isAlumni,
          sobrietyStatus,
          rcData: {
            totalScore: +row.recoveryCapitalTotal || 0,
            socialCapital: +row.socialCapitalScore || 0,
            physicalCapital: +row.physicalCapitalScore || 0,
            individualCapital: +row.individualCapitalScore || 0,
            generalCapital: +row.generalCapitalScore || 0,
            spiritualCapital: +row.spiritualCapitalScore || 0,
            therapy: row.therapy,
          },
        };
      });

    // Helper function to calculate MAT and ALL risk factor interactions
    const calculateAllRiskFactorInteractions = () => {
      const riskFactorOptions = [
        { id: "trauma", field: "childhoodTrauma", name: "Childhood Trauma" },
        {
          id: "homeless",
          field: "homelessOneYear",
          name: "Homeless more than a Year",
        },
        { id: "prison", field: "overOneYearInPrison", name: "Prison History" },
        {
          id: "diagnosis",
          field: "coOccurringDiagnosis",
          name: "Co-occurring Diagnosis",
        },
        { id: "kids", field: "kids", name: "Has Children" },
        { id: "therapy", field: "therapy", name: "In Therapy" },
      ];

      const allInteractions = {};

      riskFactorOptions.forEach((factor) => {
        let matWithFactor,
          matWithoutFactor,
          nonMatWithFactor,
          nonMatWithoutFactor;

        if (factor.id === "therapy") {
          // Special handling for therapy (from questionnaire data) - no change needed
          matWithFactor = combinedData.filter(
            (person) => person.isMAT && isInTherapy(person.rcData.therapy)
          );
          matWithoutFactor = combinedData.filter(
            (person) => person.isMAT && !isInTherapy(person.rcData.therapy)
          );
          nonMatWithFactor = combinedData.filter(
            (person) => !person.isMAT && isInTherapy(person.rcData.therapy)
          );
          nonMatWithoutFactor = combinedData.filter(
            (person) => !person.isMAT && !isInTherapy(person.rcData.therapy)
          );
        } else {
          // Standard handling for other factors - ADD FILTERING FOR KNOWN RESPONSES
          const hasKnownResponse = (row) => {
            const response = checkResponse(row[factor.field]);
            return response === "yes" || response === "no";
          };

          const checkFactor = (row) =>
            checkResponse(row[factor.field]) === "yes";

          // Filter to only include people with known responses for this specific factor
          const peopleWithKnownResponses =
            combinedData.filter(hasKnownResponse);

          matWithFactor = peopleWithKnownResponses.filter(
            (person) => person.isMAT && checkFactor(person)
          );
          matWithoutFactor = peopleWithKnownResponses.filter(
            (person) => person.isMAT && !checkFactor(person)
          );
          nonMatWithFactor = peopleWithKnownResponses.filter(
            (person) => !person.isMAT && checkFactor(person)
          );
          nonMatWithoutFactor = peopleWithKnownResponses.filter(
            (person) => !person.isMAT && !checkFactor(person)
          );
        }

        // Calculate metrics for each group (same logic as existing trauma function)
        const calculateGroupMetrics = (group) => {
          const alumniInGroup = group.filter((person) => person.isAlumni);
          const relapseCount = alumniInGroup.filter(
            (person) => person.sobrietyStatus !== "Sober"
          ).length;

          const relapseRate =
            alumniInGroup.length > 0
              ? (relapseCount / alumniInGroup.length) * 100
              : 0;

          const rcAvg =
            group.length > 0
              ? group.reduce(
                  (sum, person) => sum + person.rcData.totalScore,
                  0
                ) / group.length
              : 0;

          return {
            relapseRate: parseFloat(relapseRate.toFixed(1)),
            rcAvg: parseFloat(rcAvg.toFixed(1)),
            count: group.length,
          };
        };

        allInteractions[factor.id] = {
          name: factor.name,
          matWith: calculateGroupMetrics(matWithFactor),
          matWithout: calculateGroupMetrics(matWithoutFactor),
          noMatWith: calculateGroupMetrics(nonMatWithFactor),
          noMatWithout: calculateGroupMetrics(nonMatWithoutFactor),
        };
      });

      return allInteractions;
    };

    // Helper function to calculate MAT overview data
    // Helper function to calculate MAT overview data with separate datasets
    const calculateMatOverview = () => {
      // 1. SOBRIETY RATES: Use ALL alumni from the broader dataset (same as Quick Glance)
      // First, fetch all alumni data (not just those with RC assessments)
      const allMatAlumni = Array.from(alumniMap.values()).filter(
        (alumni) => alumni.isMAT === true
      );
      const allNonMatAlumni = Array.from(alumniMap.values()).filter(
        (alumni) => !alumni.isMAT
      );

      const allMatSoberCount = allMatAlumni.filter(
        (alumni) => alumni.sobrietyStatus === "Sober"
      ).length;

      const allNonMatSoberCount = allNonMatAlumni.filter(
        (alumni) => alumni.sobrietyStatus === "Sober"
      ).length;

      const matSobrietyRate =
        allMatAlumni.length > 0
          ? (allMatSoberCount / allMatAlumni.length) * 100
          : 0;
      const nonMatSobrietyRate =
        allNonMatAlumni.length > 0
          ? (allNonMatSoberCount / allNonMatAlumni.length) * 100
          : 0;

      // 2. RC SCORES: Use only people with assessment data (combinedData)
      const matUsersWithRC = combinedData.filter((person) => person.isMAT);
      const nonMatUsersWithRC = combinedData.filter((person) => !person.isMAT);

      const matRCAvg =
        matUsersWithRC.length > 0
          ? matUsersWithRC.reduce(
              (sum, person) => sum + person.rcData.totalScore,
              0
            ) / matUsersWithRC.length
          : 0;

      const nonMatRCAvg =
        nonMatUsersWithRC.length > 0
          ? nonMatUsersWithRC.reduce(
              (sum, person) => sum + person.rcData.totalScore,
              0
            ) / nonMatUsersWithRC.length
          : 0;

      // Log individual RC scores for MAT users
      const matScores = matUsersWithRC.map(
        (person) => person.rcData.totalScore
      );
      const nonMatScores = nonMatUsersWithRC.map(
        (person) => person.rcData.totalScore
      );

      console.log(`  MAT RC Scores:`, matScores);
      console.log(`  Non-MAT RC Scores:`, nonMatScores);

      const matRCSum = matUsersWithRC.reduce(
        (sum, person) => sum + person.rcData.totalScore,
        0
      );
      const nonMatRCSum = nonMatUsersWithRC.reduce(
        (sum, person) => sum + person.rcData.totalScore,
        0
      );

      return [
        {
          name: "With MAT",
          sobriety: parseFloat(matSobrietyRate.toFixed(1)),
          rc: parseFloat(matRCAvg.toFixed(1)),
          soberCount: allMatSoberCount,
          totalAlumni: allMatAlumni.length,
          totalUsers: matUsersWithRC.length, // This is now people with RC assessments
        },
        {
          name: "Without MAT",
          sobriety: parseFloat(nonMatSobrietyRate.toFixed(1)),
          rc: parseFloat(nonMatRCAvg.toFixed(1)),
          soberCount: allNonMatSoberCount,
          totalAlumni: allNonMatAlumni.length,
          totalUsers: nonMatUsersWithRC.length, // This is now people with RC assessments
        },
      ];
    };

    // Helper function to calculate time-dependent effects
    const calculateTimeEffects = () => {
      // Define recovery time periods (in days)
      const periods = {
        earlyRecovery: { min: 0, max: 90 },
        midRecovery: { min: 91, max: 180 },
        establishedRecovery: { min: 181, max: 365 },
        longTermRecovery: { min: 366, max: Infinity },
      };

      // Calculate days in recovery for each person using existing function
      const dataWithDays = combinedData.map((person) => {
        let daysInRecovery = 0;

        // Use the same logic as existing sobriety calculation
        const residentName =
          person.residentName ||
          person.fullName ||
          `${person.firstName} ${person.lastName}`.trim();

        const resident = residentsMap.get(residentName) || {};
        const alumni = alumniMap.get(residentName) || {};

        const sobrietyDate =
          resident?.sobrietyDate || alumni?.sobrietyDate || person.sobrietyDate;

        if (sobrietyDate) {
          const soberDate = new Date(sobrietyDate);
          const assessmentDate = person.timestamp?.toDate
            ? person.timestamp.toDate()
            : new Date(person.timestamp);

          if (!isNaN(soberDate.getTime()) && !isNaN(assessmentDate.getTime())) {
            daysInRecovery = Math.max(
              0,
              Math.floor((assessmentDate - soberDate) / (1000 * 60 * 60 * 24))
            );
          }
        }

        return {
          ...person,
          daysInRecovery,
        };
      });

      console.log("\n⏰ MAT TIME-DEPENDENT EFFECTS DEBUG:");
      console.log(
        `  Total people with days calculated: ${dataWithDays.length}`
      );
      console.log(
        `  Alumni with outcomes: ${
          dataWithDays.filter((p) => p.isAlumni).length
        }`
      );

      // Group by time period and MAT status
      const results = {};

      Object.keys(periods).forEach((period) => {
        const { min, max } = periods[period];

        const inPeriod = dataWithDays.filter(
          (person) =>
            person.daysInRecovery >= min && person.daysInRecovery <= max
        );

        const matInPeriod = inPeriod.filter((person) => person.isMAT);
        const nonMatInPeriod = inPeriod.filter((person) => !person.isMAT);

        // Calculate MAT group metrics (only alumni for relapse rates)
        const matAlumniInPeriod = matInPeriod.filter(
          (person) => person.isAlumni
        );
        const matRelapseCount = matAlumniInPeriod.filter(
          (person) => person.sobrietyStatus !== "Sober"
        ).length;

        const matRelapseRate =
          matAlumniInPeriod.length > 0
            ? (matRelapseCount / matAlumniInPeriod.length) * 100
            : 0;

        const matRCAvg =
          matInPeriod.length > 0
            ? matInPeriod.reduce(
                (sum, person) => sum + person.rcData.totalScore,
                0
              ) / matInPeriod.length
            : 0;

        // Calculate non-MAT group metrics
        const nonMatAlumniInPeriod = nonMatInPeriod.filter(
          (person) => person.isAlumni
        );
        const nonMatRelapseCount = nonMatAlumniInPeriod.filter(
          (person) => person.sobrietyStatus !== "Sober"
        ).length;

        const nonMatRelapseRate =
          nonMatAlumniInPeriod.length > 0
            ? (nonMatRelapseCount / nonMatAlumniInPeriod.length) * 100
            : 0;

        const nonMatRCAvg =
          nonMatInPeriod.length > 0
            ? nonMatInPeriod.reduce(
                (sum, person) => sum + person.rcData.totalScore,
                0
              ) / nonMatInPeriod.length
            : 0;

        results[period] = {
          mat: {
            relapseRate: parseFloat(matRelapseRate.toFixed(1)),
            rcAvg: parseFloat(matRCAvg.toFixed(1)),
            count: matInPeriod.length,
          },
          nonMat: {
            relapseRate: parseFloat(nonMatRelapseRate.toFixed(1)),
            rcAvg: parseFloat(nonMatRCAvg.toFixed(1)),
            count: nonMatInPeriod.length,
          },
        };
      });

      return results;
    };

    // Helper function to calculate MAT and trauma interaction
    const calculateTraumaInteraction = () => {
      // Group by MAT status and trauma history
      const matWithTrauma = combinedData.filter(
        (person) => person.isMAT && person.hasTrauma
      );
      const matWithoutTrauma = combinedData.filter(
        (person) => person.isMAT && person.hasTrauma === false // Only explicit "no"
      );
      const nonMatWithTrauma = combinedData.filter(
        (person) => !person.isMAT && person.hasTrauma
      );
      const nonMatWithoutTrauma = combinedData.filter(
        (person) => !person.isMAT && !person.hasTrauma
      );

      // Calculate metrics for each group
      const calculateGroupMetrics = (group, groupName) => {
        const alumniInGroup = group.filter((person) => person.isAlumni);
        const relapseCount = alumniInGroup.filter(
          (person) => person.sobrietyStatus !== "Sober"
        ).length;

        const relapseRate =
          alumniInGroup.length > 0
            ? (relapseCount / alumniInGroup.length) * 100
            : 0;

        const rcAvg =
          group.length > 0
            ? group.reduce((sum, person) => sum + person.rcData.totalScore, 0) /
              group.length
            : 0;

        return {
          relapseRate: parseFloat(relapseRate.toFixed(1)),
          rcAvg: parseFloat(rcAvg.toFixed(1)),
          count: group.length,
        };
      };

      return {
        matTrauma: calculateGroupMetrics(matWithTrauma, "MAT + Trauma"),
        matNoTrauma: calculateGroupMetrics(matWithoutTrauma, "MAT + No Trauma"),
        noMatTrauma: calculateGroupMetrics(nonMatWithTrauma, "No MAT + Trauma"),
        noMatNoTrauma: calculateGroupMetrics(
          nonMatWithoutTrauma,
          "No MAT + No Trauma"
        ),
      };
    };

    // Helper function to calculate domain impacts
    const calculateDomainImpacts = () => {
      const matUsers = combinedData.filter((person) => person.isMAT);
      const nonMatUsers = combinedData.filter((person) => !person.isMAT);

      // Define domains to compare
      const domains = [
        { name: "Social Capital", key: "socialCapital" },
        { name: "Physical Capital", key: "physicalCapital" },
        { name: "Individual Capital", key: "individualCapital" },
        { name: "General Capital", key: "generalCapital" },
        { name: "Spiritual Capital", key: "spiritualCapital" },
      ];

      return domains.map((domain) => {
        // Calculate average domain score for MAT users
        const matDomainAvg =
          matUsers.length > 0
            ? matUsers.reduce(
                (sum, person) => sum + (person.rcData[domain.key] || 0),
                0
              ) / matUsers.length
            : 0;

        // Calculate average domain score for non-MAT users
        const nonMatDomainAvg =
          nonMatUsers.length > 0
            ? nonMatUsers.reduce(
                (sum, person) => sum + (person.rcData[domain.key] || 0),
                0
              ) / nonMatUsers.length
            : 0;

        // Calculate percentage difference
        const percentDiff =
          nonMatDomainAvg > 0
            ? ((matDomainAvg - nonMatDomainAvg) / nonMatDomainAvg) * 100
            : 0;

        return {
          domain: domain.name,
          percentDiff: parseFloat(percentDiff.toFixed(1)),
        };
      });
    };

    // Execute all calculations
    const matOverview = calculateMatOverview();
    const matTimeEffects = calculateTimeEffects();
    const traumaInteraction = calculateTraumaInteraction();
    const domainImpacts = calculateDomainImpacts();

    return {
      matOverview,
      matTimeEffects,
      traumaInteraction, // Keep this for backward compatibility
      allRiskFactorInteractions: calculateAllRiskFactorInteractions(), // Add this
      domainImpacts,
    };
  } catch (error) {
    console.error("❌ Error in MAT analysis:", error);
    throw error;
  }
};

/**
 * UTILITY FUNCTIONS FOR ANALYSIS
 */
const identifyKeyStrengths = (metrics) => {
  const strengths = [];

  // Check RC performance vs internal benchmarks
  const rcPercentile =
    metrics.benchmarkComparisons?.yourPerformance?.percentileRanking;
  if (rcPercentile && metrics.rcData.rcMean >= rcPercentile.p75) {
    strengths.push("Above-average Recovery Capital scores");
  }

  // Check progression rate
  if (metrics.progressionMetrics?.averageImprovementRate > 1) {
    strengths.push("Strong resident progression tracking");
  }

  // Check internal performance ranking
  if (
    metrics.benchmarkComparisons?.internalComparison?.ranking === "Top Tier"
  ) {
    strengths.push("Top-tier internal performance");
  }

  // Check risk management effectiveness
  if (metrics.riskStratification?.riskAccuracy?.highRisk > 0.6) {
    strengths.push("Effective high-risk resident management");
  }

  // Check intervention effectiveness
  const therapyEffect = metrics.interventionEffectiveness?.therapy;
  if (therapyEffect && therapyEffect.improvement > 5) {
    strengths.push("Strong therapy program effectiveness");
  }

  // Check model accuracy
  if (metrics.successPredictionModel?.modelAccuracy > 70) {
    strengths.push("Accurate success prediction capabilities");
  }

  return strengths.slice(0, 3); // Top 3 strengths
};

const identifyTopRiskFactors = (riskMetrics) => {
  const factors = [];

  Object.entries(riskMetrics).forEach(([key, metric]) => {
    if (metric && metric.or > 1.5 && metric.strength !== "low") {
      factors.push({
        factor:
          key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
        oddsRatio: +metric.or.toFixed(2),
        strength: metric.strength,
        impact: metric.or > 3 ? "High" : metric.or > 2 ? "Moderate" : "Low",
        sampleSize: metric.sampleSizes?.with || 0,
      });
    }
  });

  return factors.sort((a, b) => b.oddsRatio - a.oddsRatio).slice(0, 3); // Top 3 risk factors
};

/**
 * RESIDENT RECOMMENDATION SYSTEM
 */
const generateResidentRecommendations = (residentId, enhancedMetrics) => {
  const resident = enhancedMetrics.rowsForStats.find(
    (r) => r.residentId === residentId
  );
  const prediction = enhancedMetrics.successPredictionModel.predictions.find(
    (p) => p.residentId === residentId
  );
  const riskProfile = enhancedMetrics.riskStratification.riskScores.find(
    (r) => r.residentId === residentId
  );

  if (!resident || !prediction || !riskProfile) {
    return { error: "Resident not found in analysis" };
  }

  const recommendations = [];

  // Risk-based recommendations
  if (riskProfile.riskLevel === "high") {
    recommendations.push({
      category: "Risk Management",
      priority: "High",
      action: "Implement intensive case management and daily check-ins",
      reason: `High risk score: ${riskProfile.riskScore}`,
    });

    riskProfile.riskFactors.forEach((factor) => {
      if (factor.includes("Trauma")) {
        recommendations.push({
          category: "Trauma Care",
          priority: "High",
          action: "Enroll in trauma-informed therapy program",
          reason: "Childhood trauma identified as risk factor",
        });
      }
      if (factor.includes("Co-occurring")) {
        recommendations.push({
          category: "Mental Health",
          priority: "High",
          action:
            "Coordinate with psychiatric services for medication management",
          reason: "Co-occurring diagnosis identified",
        });
      }
    });
  }

  // RC improvement recommendations based on domain analysis
  const lowDomains = [];
  ["spiritual", "social", "individual", "physical", "general"].forEach(
    (domain) => {
      const score = resident[`${domain}CapitalScore`] || 0;
      const avgScore =
        enhancedMetrics.domainData.find((d) =>
          d.name.toLowerCase().includes(domain)
        )?.score || 0;
      if (score < avgScore * 0.8) {
        // 20% below average
        lowDomains.push(domain);
      }
    }
  );

  if (lowDomains.length > 0) {
    recommendations.push({
      category: "Recovery Capital",
      priority: "Medium",
      action: `Focus on improving: ${lowDomains.join(", ")} capital domains`,
      reason: `Below-average scores in these domains`,
    });
  }

  // Success probability recommendations
  if (prediction.successScore < 60) {
    recommendations.push({
      category: "Success Planning",
      priority: "Medium",
      action: "Develop intensive success plan with frequent milestones",
      reason: `Success probability: ${prediction.successScore}%`,
    });
  }

  // Intervention recommendations based on effectiveness data
  const therapyEffect = enhancedMetrics.interventionEffectiveness?.therapy;
  if (
    therapyEffect &&
    !isInTherapy(resident.therapy) &&
    therapyEffect.improvement > 0
  ) {
    recommendations.push({
      category: "Intervention",
      priority: "Medium",
      action: "Consider therapy enrollment - shows positive impact",
      reason: `Therapy shows ${therapyEffect.improvement} point RC improvement on average`,
    });
  }

  return {
    residentProfile: {
      id: residentId,
      riskLevel: riskProfile.riskLevel,
      riskScore: riskProfile.riskScore,
      successProbability: prediction.successScore,
      rcScore: resident.recoveryCapitalTotal,
      riskFactors: riskProfile.riskFactors,
    },
    recommendations: recommendations
      .sort((a, b) => {
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      })
      .slice(0, 5), // Top 5 recommendations
  };
};

/**
 * EXPORT ALL FUNCTIONS
 */

export {
  // Core calculation functions
  normalizeMetric,
  buildMetric,
  calculateCorrelation,
  checkResponse,
  isRelapse,
  hasGuiltShame,
  isInTherapy,
  hasStrongKidsRelation,

  // Enhanced calculation functions
  calculateFactorCombinations,
  createDomainImpacts,
  calculateBenchmarkComparisons,
  calculateRiskStratificationScientific,
  calculateSuccessPredictionModelScientific,
  calculateProgressionMetrics,
  calculateLengthOfStayAnalysis,
  calculateInterventionEffectiveness,

  // Quality and analysis functions
  calculateProgramQualityScore,

  // Utility functions
  identifyKeyStrengths,
  identifyTopRiskFactors,
  generateResidentRecommendations,

  // Helper functions
  calculateEffectSize,
  calculatePercentile,
  getFactorColor,
};
